import React, { useState, useEffect, useCallback, useRef } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { TabBar, TabBtn } from '../../components/common/Tabs';
import { gateEntryAPI, operationsAPI, orderAPI, aiAPI } from '../../services/api';
import {
  HiOutlineCheckCircle, HiOutlineCog, HiOutlineAdjustments, HiOutlinePlus,
  HiOutlinePlay, HiOutlineArrowCircleRight, HiOutlineX, HiOutlineTrash,
  HiOutlineCube, HiOutlineClipboardList, HiOutlineClock, HiOutlineSearch, HiOutlineEye, HiOutlinePencil,
  HiOutlineClipboardCheck, HiOutlineCheck
} from 'react-icons/hi';

import toast from 'react-hot-toast';
import { io } from 'socket.io-client';
import { useAuth } from '../../context/AuthContext';
import Pagination from '../../components/common/Pagination';
import { RESIN_CHANGE_OPTIONS, EMPTY_PROCESS_SPECS, processSpecsFromJob, processSpecsToPayload, getBuildProcessSpecs, formatBuildProcessSpecs } from '../../utils/buildProcessSpecs';

// const SOCKET_URL = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`;
// const SOCKET_URL = process.env.REACT_APP_API_URL || `http://localhost:9898`;
const SOCKET_URL = process.env.REACT_APP_API_URL || (window.location.port ? `${window.location.protocol}//${window.location.hostname}:9898` : window.location.origin);

// ==========================================
// MODEL-WISE QUALITY CHECK HELPERS
// ==========================================

// Full date + time, e.g. "08 Oct 2026, 03:45 PM"
const qcFormatDateTime = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

// Human-readable gap between two timestamps, e.g. "2h 15m" / "3d 4h"
const qcFormatDuration = (fromValue, toValue) => {
  if (!fromValue || !toValue) return null;
  const from = new Date(fromValue).getTime();
  const to = new Date(toValue).getTime();
  if (isNaN(from) || isNaN(to) || to < from) return null;

  const totalMinutes = Math.round((to - from) / 60000);
  if (totalMinutes < 1) return 'Under a minute';

  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;

  const parts = [];
  if (days > 0) parts.push(days + 'd');
  if (hours > 0) parts.push(hours + 'h');
  if (minutes > 0 && days === 0) parts.push(minutes + 'm');
  return parts.join(' ') || '0m';
};

// The build's start time - explicit startedAt, else when the job was created
const qcBuildStart = (job) => job?.startedAt || job?.createdAt || null;

// Small labelled value tile used throughout the model detail view
const QcInfoTile = ({ label, value, color, mono }) => (
  <div style={{
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid var(--border)',
    borderRadius: '8px',
    padding: '10px 12px',
    minWidth: 0
  }}>
    <div style={{
      fontSize: '0.68rem', color: 'var(--text-dim)', textTransform: 'uppercase',
      letterSpacing: '0.4px', fontWeight: 700, marginBottom: '4px'
    }}>
      {label}
    </div>
    <div style={{
      fontSize: mono ? '0.82rem' : '0.88rem',
      fontWeight: 700,
      color: color || 'var(--text-primary)',
      wordBreak: 'break-word'
    }}>
      {(value === null || value === undefined || value === '') ? '--' : value}
    </div>
  </div>
);

const CountdownTimer = ({ job, onStatusChange }) => {
  const [timeLeft, setTimeLeft] = useState('');
  const hasTriggeredDelay = useRef(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      if (!job || !job.estimatedDate) return 'No ETA';

      if (job.status === 'completed') {
        return '✅ Completed';
      }
      if (job.status === 'delayed') {
        return '⚠️ Delayed';
      }
      if (job.status === 'shortage_reported') {
        return '⚠️ Shortage';
      }

      const difference = new Date(job.estimatedDate) - new Date();
      if (difference <= 0) {
        if (job.status === 'processing' && !hasTriggeredDelay.current) {
          hasTriggeredDelay.current = true;
          if (onStatusChange) {
            onStatusChange(job, 'delayed');
          }
        }
        return '⚠️ Delayed';
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((difference / 1000 / 60) % 60);
      const seconds = Math.floor((difference / 1000) % 60);

      let parts = [];
      if (days > 0) parts.push(`${days}d`);
      if (hours > 0 || days > 0) parts.push(`${hours}h`);
      parts.push(`${minutes}m`);
      parts.push(`${seconds}s`);

      return parts.join(' ');
    };

    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [job, onStatusChange]);

  const isDelayed = timeLeft.includes('Delayed') || (job && job.status === 'delayed');
  const isCompleted = timeLeft.includes('Completed') || (job && job.status === 'completed');
  const isShortage = timeLeft.includes('Shortage') || (job && job.status === 'shortage_reported');

  const badgeColor = isCompleted ? '#10b981' : (isDelayed || isShortage) ? '#ef4444' : 'var(--primary)';
  const badgeBg = isCompleted ? 'rgba(16, 185, 129, 0.08)' : (isDelayed || isShortage) ? 'rgba(239, 68, 68, 0.08)' : 'rgba(249, 115, 22, 0.08)';
  const badgeBorder = isCompleted ? '1px solid rgba(16, 185, 129, 0.2)' : (isDelayed || isShortage) ? '1px solid rgba(239, 68, 68, 0.2)' : '1px solid rgba(249, 115, 22, 0.2)';

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', color: badgeColor, background: badgeBg, padding: '6px 12px', borderRadius: '20px', border: badgeBorder, fontWeight: 600 }}>
      <HiOutlineClock style={{ fontSize: '1rem' }} />
      <span>{timeLeft}</span>
    </div>
  );
};

const SupervisorDashboard = () => {
  const { user } = useAuth();
  const subIndustryId = user?.organizationId?.subIndustryId || 'drag_chain';
  const isDragChain = subIndustryId === 'drag_chain';
  const [activeTab, setActiveTab] = useState(user?.role === 'production_manager' ? 'machines' : 'gate_approvals'); // 'gate_approvals', 'machines', 'build_queue', 'tasks'

  const [machineSearch, setMachineSearch] = useState('');
  const [historySearch, setHistorySearch] = useState('');
  const [historyPage, setHistoryPage] = useState(1);

  const [gateEntries, setGateEntries] = useState([]);
  const [gateFilter, setGateFilter] = useState('pending'); // 'pending', 'completed', 'all'
  const [machines, setMachines] = useState([]);
  const [buildJobs, setBuildJobs] = useState([]);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [shortages, setShortages] = useState([]);
  const [orderShortages, setOrderShortages] = useState([]);
  const [allSbsLogs, setAllSbsLogs] = useState([]);
  const [allInventoryItems, setAllInventoryItems] = useState([]);
  const [selectedShortage, setSelectedShortage] = useState(null);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [planForm, setPlanForm] = useState({ plannedShots: '' });
  const [aiLoading, setAiLoading] = useState(false);

  // PM Assign Supervisor Modal State
  const [showAssignSupervisorModal, setShowAssignSupervisorModal] = useState(false);
  const [selectedInventoryItem, setSelectedInventoryItem] = useState(null);
  const [selectedSupervisor, setSelectedSupervisor] = useState('Supervisor Line 1');
  const [pmAssignRemarks, setPmAssignRemarks] = useState('');

  // View Product Specs Modal State
  const [showViewSpecsModal, setShowViewSpecsModal] = useState(false);
  const [viewSpecsItem, setViewSpecsItem] = useState(null);

  const handleOpenViewSpecs = (item) => {
    setViewSpecsItem(item);
    setShowViewSpecsModal(true);
  };

  const [inventoryStatusFilter, setInventoryStatusFilter] = useState('all'); // 'all', 'pending', 'completed'
  const [inventoryPage, setInventoryPage] = useState(1);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Quality Check State in Supervisor Dashboard
  const [showSupervisorQcModal, setShowSupervisorQcModal] = useState(false);
  const [selectedSupervisorQcJob, setSelectedSupervisorQcJob] = useState(null);
  const [supervisorQcAction, setSupervisorQcAction] = useState('pass'); // 'pass', 'fail', 'other'
  const [supervisorQcForm, setSupervisorQcForm] = useState({
    passedQuantity: 0,
    failedQuantity: 0,
    remarks: ''
  });
  // Model-wise Quality Check view: blank until a model number is picked
  const [supervisorQcModel, setSupervisorQcModel] = useState('');
  const [supervisorQcPage, setSupervisorQcPage] = useState(1);
  const [supervisorQcSearch, setSupervisorQcSearch] = useState('');

  const handleAssignSupervisorSubmit = async (e) => {
    e.preventDefault();
    if (!selectedInventoryItem) return;
    setSubmitting(true);
    try {
      await operationsAPI.updateInventoryItem(selectedInventoryItem._id, {
        productionStatus: 'assigned_to_supervisor',
        assignedSupervisor: selectedSupervisor,
        pmRemarks: pmAssignRemarks || 'Urgent basis priority production required',
        assignedSupervisorAt: new Date()
      });
      toast.success(`Shortage assigned to ${selectedSupervisor} on urgent basis!`);
      setShowAssignSupervisorModal(false);
      setSelectedInventoryItem(null);
      setPmAssignRemarks('');
      fetchData();
    } catch (err) {
      toast.error('Failed to assign supervisor');
    } finally {
      setSubmitting(false);
    }
  };

  // Modals
  const [showBuildModal, setShowBuildModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showMachineModal, setShowMachineModal] = useState(false);

  // Gate Entry Verification Remark Modal
  const [showGateVerifyModal, setShowGateVerifyModal] = useState(false);
  const [selectedGateEntryId, setSelectedGateEntryId] = useState(null);
  const [verificationRemarks, setVerificationRemarks] = useState('');

  // Add Items Modal for Supervisor
  const [showAddItemsModal, setShowAddItemsModal] = useState(false);
  const [selectedEntryForItems, setSelectedEntryForItems] = useState(null);
  const [itemsForm, setItemsForm] = useState({
    name: '',
    type: 'raw_material',
    quantity: '',
    unit: '',
    location: 'Unassigned',
    size: '',
    description: ''
  });

  const [dbCategories, setDbCategories] = useState([]);

  // Dynamically constructed pure DB Machine Categories
  const MACHINE_CATEGORIES = React.useMemo(() => {
    const categories = {};

    (dbCategories || []).forEach(cat => {
      if (cat.categoryName) {
        categories[cat.categoryName] = cat.machineNames || [];
      }
    });

    (machines || []).forEach(m => {
      const cat = m.category || m.productionLine || 'General Plant Utilities';
      if (!categories[cat]) {
        categories[cat] = [];
      }
      if (m.name && !categories[cat].includes(m.name)) {
        categories[cat].push(m.name);
      }
    });

    return categories;
  }, [dbCategories, machines]);

  const PREDEFINED_MACHINES = React.useMemo(() => {
    const set = new Set();
    Object.values(MACHINE_CATEGORIES).forEach(list => list.forEach(item => set.add(item)));
    (machines || []).forEach(m => { if (m.name) set.add(m.name); });
    return Array.from(set);
  }, [MACHINE_CATEGORIES, machines]);

  // Machine Form
  const [machineForm, setMachineForm] = useState({ name: '', status: 'available', remarks: '', capacity: '', modelNumber: '', powerRating: '', lastServiceDate: '', productionLine: 'Line 1' });
  const [editingMachineId, setEditingMachineId] = useState(null);
  const [selectedMachineType, setSelectedMachineType] = useState('');
  const [isCustomMachineName, setIsCustomMachineName] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubMachine, setSelectedSubMachine] = useState('');
  const [showCustomDropdown, setShowCustomDropdown] = useState(false);
  const [activeHoverCategory, setActiveHoverCategory] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowCustomDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Build Form
  const [buildModalMode, setBuildModalMode] = useState('create'); // 'create' or 'edit'
  const [selectedJobId, setSelectedJobId] = useState('');
  const [buildStatus, setBuildStatus] = useState('processing');
  const [estimatedDate, setEstimatedDate] = useState(new Date().toISOString().split('T')[0]);
  const [buildTime, setBuildTime] = useState(new Date().toTimeString().slice(0, 5));
  const [materialsUsed, setMaterialsUsed] = useState([{ materialName: '', quantity: 25, unit: 'kg' }]);
  const [customFields, setCustomFields] = useState([]);
  const [selectedMachineId, setSelectedMachineId] = useState('');
  const [processSpecs, setProcessSpecs] = useState(EMPTY_PROCESS_SPECS);

  // Build completion: batches/shots are asked here, right before Quality Check
  const [showCompleteBuildModal, setShowCompleteBuildModal] = useState(false);
  const [completingJob, setCompletingJob] = useState(null);
  const [completeForm, setCompleteForm] = useState({ completedShots: '', completionRemarks: '' });

  // Material Inventory CRUD Form States
  const [materialSearch, setMaterialSearch] = useState('');
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [editingMaterialId, setEditingMaterialId] = useState(null);
  const [materialForm, setMaterialForm] = useState({
    name: '',
    type: 'raw_material',
    quantity: 0,
    unit: 'kg',
    location: 'Unassigned',
    size: '',
    description: ''
  });

  // Automatically update selectedJob in create mode when a job is picked from the dropdown
  useEffect(() => {
    if (buildModalMode === 'create') {
      if (selectedJobId) {
        if (selectedJobId.startsWith('shortage_')) {
          return;
        }
        const job = buildJobs.find(j => j._id === selectedJobId);
        setSelectedJob(job || null);
      } else {
        setSelectedJob(null);
      }
    }
  }, [selectedJobId, buildJobs, buildModalMode]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [gateRes, machRes, buildRes, invRes, shortageRes, orderShortRes] = await Promise.all([
        gateEntryAPI.getAll({ limit: 100 }),
        operationsAPI.getMachines(),
        operationsAPI.getBuildJobs(),
        operationsAPI.getInventory(),
        operationsAPI.getShortageBuySales(),
        orderAPI.getShortages()
      ]);
      setGateEntries(gateRes.data.data);
      setMachines(machRes.data.data);
      setBuildJobs(buildRes.data.data);
      setAllInventoryItems(invRes.data.data);
      setRawMaterials(invRes.data.data.filter(item => item.type === 'raw_material'));
      // Filter shortage logs assigned to supervisor or production manager
      setShortages(shortageRes.data.data.filter(item => item.assignedTo === 'supervisor' || item.assignedTo === 'production_manager'));
      setAllSbsLogs(shortageRes.data.data);
      setOrderShortages(orderShortRes.data.data);
    } catch (err) {
      toast.error('Failed to load supervisor workspace data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();

    // Listen to real-time events to reload queue
    let socket;
    if (user?.organizationId) {
      socket = io(SOCKET_URL, { withCredentials: true });
      socket.emit('join_org', user.organizationId);

      socket.on('gate_entry_updated', fetchData);
      socket.on('build_updated', fetchData);
      socket.on('machine_updated', fetchData);
      socket.on('communication_updated', fetchData);
      socket.on('inventory_updated', fetchData);
      socket.on('qc_updated', fetchData);
      socket.on('product_sent_to_store', fetchData);
      socket.on('shortage_updated', fetchData);
    }

    return () => {
      if (socket) socket.disconnect();
    };
  }, [fetchData, user?.organizationId]);

  const getRequiredQty = useCallback((item) => {
    if (!item) return 0;
    let required = 0;
    const itemIdStr = item._id ? item._id.toString() : '';
    const itemNameLower = (item.name || item.itemName || '').toLowerCase().trim();
    const itemModelLower = (item.modelNumber || '').toLowerCase().trim();

    // 1. Calculate from active buildJobs (Order Product Build)
    buildJobs.forEach(job => {
      if (['pending', 'processing', 'shortage_reported', 'assigned'].includes(job.status)) {
        const jobInvId = job.inventoryItemId ? (job.inventoryItemId._id || job.inventoryItemId).toString() : '';
        const matchesId = itemIdStr && jobInvId && itemIdStr === jobInvId;
        const jobNameLower = (job.productName || '').toLowerCase().trim();
        const jobModelLower = (job.modelNumber || '').toLowerCase().trim();
        const matchesName = itemNameLower && (jobNameLower.includes(itemNameLower) || itemNameLower.includes(jobNameLower));
        const matchesModel = itemModelLower && (jobModelLower.includes(itemModelLower) || itemModelLower.includes(jobModelLower));

        if (matchesId || matchesName || matchesModel) {
          required += Number(job.orderQuantity) || 0;
        }
      }
    });

    // 2. Calculate from allSbsLogs (ShortageBuySale)
    allSbsLogs.forEach(log => {
      if (['shortage', 'buy'].includes(log.type) && ['pending', 'assigned', 'ordered'].includes(log.status)) {
        const logInvId = log.inventoryItemId ? (log.inventoryItemId._id || log.inventoryItemId).toString() : '';
        const matchesId = itemIdStr && logInvId && itemIdStr === logInvId;
        const logItemLower = (log.itemName || '').toLowerCase().trim();
        if (matchesId || (logItemLower && (itemNameLower && (logItemLower.includes(itemNameLower) || itemNameLower.includes(logItemLower)) || (itemModelLower && (logItemLower.includes(itemModelLower) || itemModelLower.includes(logItemLower)))))) {
          required += Number(log.quantity) || 0;
        }
      }
    });

    // 3. Calculate from orderShortages (ShortageReport)
    orderShortages.forEach(s => {
      if (['pending_planning', 'pending'].includes(s.status) && s.modelNumber) {
        const sModelLower = s.modelNumber.toLowerCase().trim();
        const matchesName = itemNameLower && (sModelLower.includes(itemNameLower) || itemNameLower.includes(sModelLower));
        const matchesModel = itemModelLower && (sModelLower.includes(itemModelLower) || itemModelLower.includes(sModelLower));
        if (matchesName || matchesModel) {
          required += Number(s.shortageMeters) || 0;
        }
      }
    });

    return required;
  }, [buildJobs, allSbsLogs, orderShortages]);

  // Order Shortage Planning Handlers
  const handlePlanShortage = async (e) => {
    e.preventDefault();
    if (!selectedShortage) return;
    setSubmitting(true);
    try {
      await orderAPI.planShortage(selectedShortage._id, planForm);
      toast.success('Production Plan generated successfully');
      setShowPlanModal(false);
      setPlanForm({ plannedShots: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to plan production');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAskAI = async () => {
    if (!selectedShortage) return;
    setAiLoading(true);
    try {
      const res = await aiAPI.predictShots({
        modelNumber: selectedShortage.modelNumber,
        shortageMeters: selectedShortage.shortageMeters
      });
      const data = res.data.data;
      setPlanForm({ plannedShots: data.shots_needed });
      toast.success(`AI calculated ${data.shots_needed} shots needed. Requires ${data.nylon_kg}kg Nylon and ~${data.time_hrs}hrs.`);
    } catch (err) {
      toast.error('AI Calculation Failed');
    } finally {
      setAiLoading(false);
    }
  };

  // Open Gate Verify Modal
  const handleOpenGateVerify = (id) => {
    setSelectedGateEntryId(id);
    setVerificationRemarks('');
    setShowGateVerifyModal(true);
  };

  // Gate Approval Handler
  const handleApproveGate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await gateEntryAPI.update(selectedGateEntryId, {
        status: 'verified',
        remarks: verificationRemarks,
        verifiedBy: user._id
      });
      toast.success('Gate Entry approved & verified with remarks successfully');
      setShowGateVerifyModal(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to approve Gate Entry');
    } finally {
      setSubmitting(false);
    }
  };

  const openAddItemsModal = (entry) => {
    setSelectedEntryForItems(entry);
    setItemsForm({
      name: entry.materialType ? entry.materialType.charAt(0).toUpperCase() + entry.materialType.slice(1) : '',
      type: 'raw_material',
      quantity: entry.quantity,
      unit: entry.unit || 'kg',
      location: 'Unassigned',
      size: '',
      description: entry.materialDescription || ''
    });
    setShowAddItemsModal(true);
  };

  const handleSaveAndVerify = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await operationsAPI.createInventoryItem({
        name: itemsForm.name,
        type: itemsForm.type,
        quantity: Number(itemsForm.quantity),
        unit: itemsForm.unit,
        location: itemsForm.location,
        size: itemsForm.size,
        description: itemsForm.description
      });

      await gateEntryAPI.verify(selectedEntryForItems._id);

      toast.success('Inventory item created and Gate Entry verified successfully!');
      setShowAddItemsModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  // Machine Handlers
  const handleSaveMachine = async (e) => {
    e.preventDefault();
    if (!machineForm.name.trim()) {
      toast.error('Please specify a machine name');
      return;
    }
    setSubmitting(true);
    const payload = {
      ...machineForm,
      category: selectedCategory && selectedCategory !== 'custom' ? selectedCategory : (machineForm.category || 'General Machinery')
    };
    try {
      if (editingMachineId) {
        await operationsAPI.updateMachine(editingMachineId, payload);
        toast.success('Machine status updated');
      } else {
        await operationsAPI.createMachine(payload);
        toast.success('Machine added to directory');
      }
      setMachineForm({ name: '', status: 'available', remarks: '', capacity: '', modelNumber: '', powerRating: '', lastServiceDate: '', productionLine: 'Line 1' });
      setEditingMachineId(null);
      setSelectedMachineType('');
      setSelectedCategory('');
      setSelectedSubMachine('');
      setIsCustomMachineName(false);
      setShowCustomDropdown(false);
      setActiveHoverCategory(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to save machine');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditMachine = (mach) => {
    setMachineForm({
      name: mach.name,
      status: mach.status,
      remarks: mach.remarks || '',
      capacity: mach.capacity || '',
      modelNumber: mach.modelNumber || '',
      powerRating: mach.powerRating || '',
      lastServiceDate: mach.lastServiceDate ? new Date(mach.lastServiceDate).toISOString().split('T')[0] : '',
      productionLine: mach.productionLine || 'Line 1'
    });
    setEditingMachineId(mach._id);

    let foundCategory = '';
    let foundSub = '';

    for (const [cat, sublist] of Object.entries(MACHINE_CATEGORIES)) {
      if (sublist.includes(mach.name)) {
        foundCategory = cat;
        foundSub = mach.name;
        break;
      }
    }

    if (foundCategory) {
      setSelectedCategory(foundCategory);
      setSelectedSubMachine(foundSub);
      setActiveHoverCategory(foundCategory);
      setIsCustomMachineName(false);
    } else {
      setSelectedCategory('custom');
      setSelectedSubMachine('');
      setActiveHoverCategory('custom');
      setIsCustomMachineName(true);
    }

    setShowMachineModal(true);
  };

  const handleDeleteMachine = async (id) => {
    if (!window.confirm('Delete this machine?')) return;
    try {
      await operationsAPI.deleteMachine(id);
      toast.success('Machine deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete machine');
    }
  };

  // Build Queue Handlers
  const handleOpenCreateBuild = () => {
    setBuildModalMode('create');
    setSelectedJobId('');
    setSelectedJob(null);
    setEstimatedDate(new Date().toISOString().split('T')[0]);
    setBuildTime(new Date().toTimeString().slice(0, 5));
    setMaterialsUsed([{ materialName: rawMaterials[0]?.name || 'Unspecified Raw Material', quantity: 25, unit: 'kg' }]);
    setCustomFields([]);
    setSelectedMachineId('');
    setProcessSpecs(EMPTY_PROCESS_SPECS);
    setShowBuildModal(true);
  };
  const handleOpenBuildFromShortage = (item) => {
    setBuildModalMode('create');
    const nameVal = item.itemName || item.productName || item.name || 'Drag Chain';
    const qtyVal = item.quantity || item.orderQuantity || 1;
    const modelVal = item.modelNumber || (nameVal.match(/STAR[A-Za-z0-9.]+/)?.[0]) || '';

    const tempJob = {
      _id: `shortage_${item._id}`,
      productName: nameVal,
      orderQuantity: qtyVal,
      productSize: item.size || item.productSize || 'Standard',
      description: item.description || '',
      modelNumber: modelVal,
      radius: item.radius || 'N/A',
      designType: item.designType || 'OP',
      vs: item.vs || 'N/A',
      hs: item.hs || 'N/A',
      sr: item.sr || 'N/A',
      status: 'pending',
      shortageLogId: item._id
    };
    setSelectedJobId(tempJob._id);
    setSelectedJob(tempJob);
    setEstimatedDate(new Date().toISOString().split('T')[0]);
    setBuildTime(new Date().toTimeString().slice(0, 5));

    const matchingMat = rawMaterials.find(m => 
      m.name === nameVal || 
      m.modelNumber === modelVal ||
      (m.modelNumber && modelVal && m.modelNumber.toLowerCase().includes(modelVal.toLowerCase()))
    );
    const chosenMatName = matchingMat ? matchingMat.name : (modelVal || (rawMaterials[0]?.name || nameVal));

    setMaterialsUsed([{ materialName: chosenMatName, quantity: 25, unit: 'kg' }]);
    setCustomFields([]);
    setSelectedMachineId('');
    setProcessSpecs(EMPTY_PROCESS_SPECS);
    setShowBuildModal(true);
  };

  const handleOpenBuild = (job) => {
    setBuildModalMode('create');
    setSelectedJobId(job._id);
    setSelectedJob(job);
    setEstimatedDate(new Date().toISOString().split('T')[0]);
    setBuildTime(new Date().toTimeString().slice(0, 5));
    setMaterialsUsed([{ materialName: rawMaterials[0]?.name || 'Unspecified Raw Material', quantity: 25, unit: 'kg' }]);
    setCustomFields([]);
    setSelectedMachineId('');
    setProcessSpecs(EMPTY_PROCESS_SPECS);
    setShowBuildModal(true);
  };

  const handleOpenEditBuild = (job) => {
    setBuildModalMode('edit');
    setSelectedJobId(job._id);
    setSelectedJob(job);
    setBuildStatus(job.status);

    if (job.estimatedDate) {
      const d = new Date(job.estimatedDate);
      setEstimatedDate(d.toISOString().split('T')[0]);
      setBuildTime(d.toTimeString().slice(0, 5));
    } else {
      setEstimatedDate(new Date().toISOString().split('T')[0]);
      setBuildTime(new Date().toTimeString().slice(0, 5));
    }

    if (job.materialsUsed && job.materialsUsed.length > 0) {
      setMaterialsUsed(job.materialsUsed.map(m => ({
        materialName: m.materialName,
        quantity: m.quantity,
        unit: m.unit
      })));
    } else {
      setMaterialsUsed([{ materialName: rawMaterials[0]?.name || 'Unspecified Raw Material', quantity: 25, unit: 'kg' }]);
    }

    if (job.customFields && job.customFields.length > 0) {
      setCustomFields(job.customFields.map(f => ({
        key: f.key,
        value: f.value
      })));
    } else {
      setCustomFields([]);
    }

    if (job.machineId) {
      setSelectedMachineId(job.machineId._id || job.machineId);
    } else {
      setSelectedMachineId('');
    }

    setProcessSpecs(processSpecsFromJob(job));

    setShowBuildModal(true);
  };

  const handleDeleteBuildJob = async (jobId) => {
    if (window.confirm('Are you sure you want to delete this manufacturing build queue entry?')) {
      try {
        await operationsAPI.deleteBuildJob(jobId);
        toast.success('Manufacturing queue entry deleted successfully!');
        fetchData();
      } catch (err) {
        toast.error('Failed to delete manufacturing queue entry');
      }
    }
  };

  // Admin Edit & Delete Shortage Log Handlers
  const [showShortageEditModal, setShowShortageEditModal] = useState(false);
  const [editingShortageLog, setEditingShortageLog] = useState(null);
  const [shortageEditForm, setShortageEditForm] = useState({
    itemName: '',
    quantity: 1,
    unit: 'pcs',
    assignedTo: 'supervisor',
    status: 'assigned',
    remarks: ''
  });

  const handleOpenEditShortageLog = (item) => {
    setEditingShortageLog(item);
    setShortageEditForm({
      itemName: item.itemName || item.name || '',
      quantity: item.quantity || 1,
      unit: item.unit || 'pcs',
      assignedTo: item.assignedTo || 'supervisor',
      status: item.status || 'assigned',
      remarks: item.remarks || ''
    });
    setShowShortageEditModal(true);
  };

  const handleSaveEditShortageLog = async (e) => {
    e.preventDefault();
    if (!editingShortageLog) return;
    setSubmitting(true);
    try {
      await operationsAPI.updateShortageBuySale(editingShortageLog._id, shortageEditForm);
      toast.success('Shortage log updated successfully!');
      setShowShortageEditModal(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to update shortage log');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteShortageLog = async (id) => {
    if (window.confirm('Are you sure you want to delete this shortage log entry?')) {
      try {
        await operationsAPI.deleteShortageBuySale(id);
        toast.success('Shortage log deleted successfully!');
        fetchData();
      } catch (err) {
        toast.error('Failed to delete shortage log entry');
      }
    }
  };

  const handleAddCustomField = () => {
    setCustomFields([...customFields, { key: '', value: '' }]);
  };

  const handleRemoveCustomField = (index) => {
    setCustomFields(customFields.filter((_, i) => i !== index));
  };

  const handleAddMaterial = () => {
    setMaterialsUsed([...materialsUsed, { materialName: rawMaterials[0]?.name || 'Unspecified Raw Material', quantity: 1, unit: 'kg' }]);
  };

  const handleRemoveMaterial = (index) => {
    setMaterialsUsed(materialsUsed.filter((_, i) => i !== index));
  };

  const handleStartProcessBuild = async (e) => {
    e.preventDefault();
    if (!selectedJob) {
      toast.error('Please select a build order.');
      return;
    }
    if (!selectedMachineId) {
      toast.error('Please assign a machine for this build.');
      return;
    }
    setSubmitting(true);
    try {
      const selectedMachine = machines.find(m => m._id === selectedMachineId);
      const machName = selectedMachine?.name || '';
      const prodLine = selectedMachine?.productionLine || 'Line 1';

      const payload = {
        status: buildModalMode === 'create' ? 'processing' : buildStatus,
        estimatedDate: estimatedDate ? new Date(`${estimatedDate}T${buildTime}:00`) : undefined,
        materialsUsed,
        customFields,
        machineId: selectedMachineId,
        machineName: machName,
        productionLine: prodLine,
        ...processSpecsToPayload(processSpecs)
      };

      if (buildModalMode === 'create') {
        payload.startedAt = Date.now();
      }

      if (selectedJob._id.toString().startsWith('shortage_')) {
        const createPayload = {
          productName: selectedJob.productName,
          productSize: selectedJob.productSize || 'Standard',
          orderQuantity: selectedJob.orderQuantity,
          status: 'processing',
          estimatedDate: payload.estimatedDate,
          materialsUsed: payload.materialsUsed,
          customFields: payload.customFields,
          machineId: payload.machineId,
          machineName: payload.machineName,
          productionLine: payload.productionLine,
          targetSetupTimeSec: payload.targetSetupTimeSec,
          resinChange: payload.resinChange,
          dieShotReq: payload.dieShotReq,
          operatorName: payload.operatorName,
          shortageLogId: selectedJob.shortageLogId,
          startedAt: Date.now()
        };
        await operationsAPI.createBuildJob(createPayload);
        // Also update the shortage log status to 'assigned'
        await operationsAPI.updateShortageBuySale(selectedJob.shortageLogId, { status: 'assigned' });
        toast.success('Production build process created from shortage log!');
      } else {
        await operationsAPI.updateBuildJob(selectedJob._id, payload);
        toast.success(
          buildModalMode === 'create'
            ? 'Product has been loaded into production line!'
            : 'Build process configuration updated!'
        );
      }
      setShowBuildModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Failed to update build process');
    } finally {
      setSubmitting(false);
    }
  };

  // Step 1: supervisor declares how many batches / shots the build actually took
  const handleOpenCompleteBuild = (job) => {
    setCompletingJob(job);
    setCompleteForm({ completedShots: '', completionRemarks: '' });
    setShowCompleteBuildModal(true);
  };

  // Step 2: record it and hand the build over to Quality Check
  const handleSubmitCompleteBuild = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!completingJob) return;

    const shots = Number(completeForm.completedShots);
    if (!completeForm.completedShots || isNaN(shots) || shots < 1) {
      toast.error('Please enter the number of batches / shots completed (minimum 1).');
      return;
    }

    setSubmitting(true);
    try {
      const res = await operationsAPI.completeBuildJob(completingJob._id, {
        completedShots: shots,
        completionRemarks: completeForm.completionRemarks.trim()
      });
      toast.success(res.data?.message || 'Production build completed and sent to Quality Check!');
      setShowCompleteBuildModal(false);
      setCompletingJob(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to complete build job');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendToStore = async (id) => {
    try {
      await operationsAPI.sendToStore(id);
      toast.success('Product completed and dispatched to Store (Handshake Sent)!');
      fetchData();
    } catch (err) {
      toast.error('Failed to complete build job');
    }
  };

  const handleOpenSupervisorQC = (job, actionType) => {
    setSelectedSupervisorQcJob(job);
    setSupervisorQcAction(actionType);
    const totalQty = Number(job.orderQuantity) || 0;
    if (actionType === 'pass') {
      setSupervisorQcForm({
        passedQuantity: totalQty,
        failedQuantity: 0,
        remarks: ''
      });
    } else if (actionType === 'fail') {
      setSupervisorQcForm({
        passedQuantity: 0,
        failedQuantity: totalQty,
        remarks: ''
      });
    } else {
      setSupervisorQcForm({
        passedQuantity: '',
        failedQuantity: '',
        remarks: ''
      });
    }
    setShowSupervisorQcModal(true);
  };

  const handleSwitchSupervisorQcAction = (actionType) => {
    setSupervisorQcAction(actionType);
    const totalQty = Number(selectedSupervisorQcJob?.orderQuantity) || 0;
    if (actionType === 'pass') {
      setSupervisorQcForm(prev => ({
        ...prev,
        passedQuantity: totalQty,
        failedQuantity: 0
      }));
    } else if (actionType === 'fail') {
      setSupervisorQcForm(prev => ({
        ...prev,
        passedQuantity: 0,
        failedQuantity: totalQty
      }));
    } else {
      setSupervisorQcForm(prev => ({
        ...prev,
        passedQuantity: '',
        failedQuantity: ''
      }));
    }
  };

  const handleSubmitSupervisorQC = async (e) => {
    e.preventDefault();
    if (!supervisorQcForm.remarks || !supervisorQcForm.remarks.trim()) {
      toast.error('QC Remarks are mandatory and strictly required!');
      return;
    }
    const passed = Number(supervisorQcForm.passedQuantity) || 0;
    const failed = Number(supervisorQcForm.failedQuantity) || 0;
    const total = passed + failed;
    if (total === 0 && Number(selectedSupervisorQcJob?.orderQuantity) > 0) {
      toast.error('Please enter valid passed or failed quantity.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await operationsAPI.processBuildJobQC(selectedSupervisorQcJob._id, {
        outcome: supervisorQcAction,
        passedQuantity: passed,
        failedQuantity: failed,
        remarks: supervisorQcForm.remarks.trim()
      });
      toast.success(res.data.message || 'Quality check processed and passed stock sent to Store!');
      setShowSupervisorQcModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to process quality check');
    } finally {
      setSubmitting(false);
    }
  };

  const reportShortage = async (job) => {
    try {
      await operationsAPI.updateBuildJob(job._id, { status: 'shortage_reported' });
      toast.success('Shortage report sent to Sales and Store Manager');
      fetchData();
    } catch (err) {
      toast.error('Failed to report shortage');
    }
  };

  const handleSaveMaterial = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingMaterialId) {
        await operationsAPI.updateInventoryItem(editingMaterialId, materialForm);
        toast.success('Material inventory item updated');
      } else {
        await operationsAPI.createInventoryItem(materialForm);
        toast.success('Material inventory item created');
      }
      setShowMaterialModal(false);
      setMaterialForm({ name: '', type: 'raw_material', quantity: 0, unit: 'kg', location: 'Unassigned', size: '', description: '' });
      setEditingMaterialId(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to save material');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditMaterial = (item) => {
    setEditingMaterialId(item._id);
    setMaterialForm({
      name: item.name,
      type: item.type || 'raw_material',
      quantity: item.quantity,
      unit: item.unit || 'kg',
      location: item.location || 'Unassigned',
      size: item.size || '',
      description: item.description || ''
    });
    setShowMaterialModal(true);
  };

  const handleDeleteMaterialItem = async (id) => {
    if (!window.confirm('Are you sure you want to delete this material record?')) return;
    try {
      await operationsAPI.deleteInventoryItem(id);
      toast.success('Material record deleted successfully');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete material record');
    }
  };

  const handleStatusChange = async (job, newStatus) => {
    try {
      const payload = { status: newStatus };
      if (['processing', 'delayed'].includes(newStatus) && !job.machineId) {
        toast.error('Please assign a machine first by clicking "Start Build" or "Edit Process".');
        return;
      }
      await operationsAPI.updateBuildJob(job._id, payload);

      if (job.shortageLogId) {
        if (newStatus === 'completed') {
          await operationsAPI.updateShortageBuySale(job.shortageLogId, { status: 'completed' });
        }
      }

      toast.success(`Build status updated to ${newStatus.replace('_', ' ')}!`);
      fetchData();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const groupBuildJobsByModel = (jobsList) => {
    const map = new Map();
    jobsList.forEach(job => {
      // Keep QC rework jobs separate so supervisor sees exact defect rebuild orders with distinct quantities
      const modelKey = job.isRework
        ? `REWORK_${job._id}`
        : ((job.modelNumber && job.modelNumber.trim()) 
            ? job.modelNumber.trim().toUpperCase()
            : (job.productName ? job.productName.trim().toUpperCase() : job._id.toString()));

      if (!map.has(modelKey)) {
        map.set(modelKey, {
          key: modelKey,
          jobs: [],
          totalQty: 0,
          primaryJob: job,
          sizes: new Set(),
          orderedByNames: new Set()
        });
      }

      const group = map.get(modelKey);
      group.jobs.push(job);
      group.totalQty += Number(job.orderQuantity) || 0;
      if (job.productSize) group.sizes.add(job.productSize);
      const salesPerson = job.salesPersonName || job.orderedBy?.name;
      if (salesPerson) group.orderedByNames.add(salesPerson);

      const statusPriority = { processing: 4, delayed: 4, shortage_reported: 3, pending: 2, completed: 1, received: 1, declined: 0 };
      if ((statusPriority[job.status] || 0) > (statusPriority[group.primaryJob.status] || 0)) {
        group.primaryJob = job;
      }
    });

    return Array.from(map.values()).map(group => ({
      ...group.primaryJob,
      orderQuantity: group.totalQty,
      productSize: Array.from(group.sizes).join(', ') || group.primaryJob.productSize || 'N/A',
      orderedByName: Array.from(group.orderedByNames).join(', ') || group.primaryJob.salesPersonName || group.primaryJob.orderedBy?.name || 'Sales Officer',
      groupedCount: group.jobs.length
    }));
  };

  const groupedBuildJobs = groupBuildJobsByModel(buildJobs);

  return (
    <DashboardLayout pageTitle="Supervisor Operations Control">

      {/* Tabs */}
      <TabBar>
        {user?.role !== 'production_manager' && (
          <TabBtn active={activeTab === 'gate_approvals'} onClick={() => setActiveTab('gate_approvals')} icon={<HiOutlineCheckCircle />}>
            Gate Approvals ({
              gateEntries.filter(entry => {
                if (gateFilter === 'pending') return entry.status === 'pending';
                if (gateFilter === 'completed') return ['verified', 'grn_created', 'rejected'].includes(entry.status);
                return true;
              }).length
            })
          </TabBtn>
        )}
        <TabBtn active={activeTab === 'machines'} onClick={() => setActiveTab('machines')} icon={<HiOutlineCog />}>
          Machine Automation ({machines.length})
        </TabBtn>
        {user?.role !== 'production_manager' && (
          <TabBtn active={activeTab === 'build_queue'} onClick={() => setActiveTab('build_queue')} icon={<HiOutlineAdjustments />}>
            Product Build Queue ({groupedBuildJobs.length})
          </TabBtn>
        )}
        {user?.role !== 'production_manager' && (
          <TabBtn active={activeTab === 'tasks'} onClick={() => setActiveTab('tasks')} icon={<HiOutlineClipboardList />}>
            Assigned Tasks ({
              shortages.filter(s => {
                if (s.status === 'completed' || s.status === 'ordered') return false;
                const linkedJob = buildJobs.find(j => j.shortageLogId === s._id);
                if (linkedJob && ['completed', 'received', 'declined'].includes(linkedJob.status)) return false;
                return true;
              }).length + allInventoryItems.filter(item => {
                if (item.productionStatus !== 'assigned_to_supervisor') return false;
                const linkedJob = buildJobs.find(j => j.shortageLogId === item._id);
                if (linkedJob && ['completed', 'received', 'declined'].includes(linkedJob.status)) return false;
                return true;
              }).length
            })
          </TabBtn>
        )}
        <TabBtn active={activeTab === 'quality_check'} onClick={() => setActiveTab('quality_check')} icon={<HiOutlineClipboardCheck />}>
          Quality Check ({buildJobs.filter(j => j.status === 'completed' && (!j.qcStatus || j.qcStatus === 'pending_qc')).length})
        </TabBtn>
        {user?.role === 'production_manager' && (
          <TabBtn active={activeTab === 'order_history'} onClick={() => setActiveTab('order_history')} icon={<HiOutlineClipboardList />}>
            Order Live & History ({buildJobs.length})
          </TabBtn>
        )}
        <TabBtn active={activeTab === 'inventory_shortage'} onClick={() => setActiveTab('inventory_shortage')} icon={<HiOutlineCube />}>
          Unified Inventory Shortage ({allInventoryItems.filter(item => item.productionStatus && item.productionStatus !== 'none').length})
        </TabBtn>
        <TabBtn active={activeTab === 'materials'} onClick={() => setActiveTab('materials')} icon={<HiOutlineCube />}>
          Material Inventory ({rawMaterials.length})
        </TabBtn>
      </TabBar>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-dim)' }}>
          <div className="spinner" style={{ margin: '0 auto 12px' }} /> Loading...
        </div>
      ) : (activeTab === 'gate_approvals' && user?.role !== 'production_manager') ? (

        /* GATE APPROVALS */
        <div className="azure-card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ color: 'var(--text-primary)', fontSize: '1.1rem', margin: 0 }}>
              {gateFilter === 'pending' ? 'Pending Gate Approvals' : gateFilter === 'completed' ? 'Completed Gate Approvals' : 'All Gate Approvals'}
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Status Filter:</span>
              <select
                value={gateFilter}
                onChange={e => setGateFilter(e.target.value)}
                style={{
                  padding: '6px 12px',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  borderRadius: '4px',
                  background: '#1e293b',
                  color: '#ffffff',
                  border: '1px solid var(--border)',
                  cursor: 'pointer',
                  outline: 'none',
                  width: '180px'
                }}
              >
                <option value="pending" style={{ backgroundColor: '#1e293b', color: '#ffffff' }}>Pending Approvals</option>
                <option value="completed" style={{ backgroundColor: '#1e293b', color: '#ffffff' }}>Completed Approvals</option>
                <option value="all" style={{ backgroundColor: '#1e293b', color: '#ffffff' }}>All Approvals</option>
              </select>
            </div>
          </div>

          {gateEntries.filter(entry => {
            if (gateFilter === 'pending') return entry.status === 'pending';
            if (gateFilter === 'completed') return ['verified', 'grn_created', 'rejected'].includes(entry.status);
            return true;
          }).length === 0 ? (
            <p style={{ color: 'var(--text-dim)', textAlign: 'center', padding: '20px' }}>
              {gateFilter === 'pending' ? 'No pending gate entry approvals.' : gateFilter === 'completed' ? 'No completed approvals found.' : 'No gate entries found.'}
            </p>
          ) : (
            <div className="azure-table-container">
              <table className="azure-table">
                <thead>
                  <tr>
                    <th>Bill Info</th>
                    <th>Vendor</th>
                    <th>Material Type</th>
                    <th>Quantity</th>
                    <th>Driver / Vehicle</th>
                    <th>Invoice Document</th>
                    {gateFilter === 'pending' ? (
                      <th>Actions</th>
                    ) : (
                      <>
                        <th>Verifier Info</th>
                        <th>Remarks / Notes</th>
                        <th>Status</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {gateEntries.filter(entry => {
                    if (gateFilter === 'pending') return entry.status === 'pending';
                    if (gateFilter === 'completed') return ['verified', 'grn_created', 'rejected'].includes(entry.status);
                    return true;
                  }).map(entry => (
                    <tr key={entry._id}>
                      <td>
                        <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{entry.billNumber}</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)', marginTop: '4px', whiteSpace: 'nowrap' }}>
                          ⏱️ {new Date(entry.createdAt).toLocaleString()}
                        </div>
                        {entry.remarks && (
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginTop: '4px', fontStyle: 'italic' }}>
                            💬 {entry.remarks}
                          </div>
                        )}
                      </td>
                      <td>{entry.vendorName}</td>
                      <td>{entry.materialType}</td>
                      <td style={{ fontWeight: 700, color: 'var(--primary-light)' }}>{entry.quantity} {entry.unit}</td>
                      <td>{entry.driverName} ({entry.vehicleNumber})</td>
                      <td>
                        {entry.invoiceUrl ? (
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <a
                              href={entry.invoiceUrl ? `${SOCKET_URL}/api/upload/view/${entry.invoiceUrl.split('/').pop()}` : '#'}
                              target="_blank"
                              rel="noreferrer"
                              className="btn btn-secondary btn-sm"
                              style={{ padding: '2px 8px', fontSize: '0.72rem', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                            >
                              View
                            </a>
                            <a
                              href={`${SOCKET_URL}/api/upload/download/${entry.invoiceUrl.split('/').pop()}`}
                              download
                              target="_blank"
                              rel="noreferrer"
                              className="btn btn-secondary btn-sm"
                              style={{ padding: '2px 8px', fontSize: '0.72rem', display: 'inline-flex', alignItems: 'center', gap: '4px', borderColor: 'var(--success)', color: 'var(--success)' }}
                            >
                              Download
                            </a>
                          </div>
                        ) : (
                          <span style={{ color: 'var(--text-dim)', fontSize: '0.8rem' }}>No Invoice</span>
                        )}
                      </td>
                      {gateFilter === 'pending' ? (
                        <td>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'nowrap' }}>
                            <button className="btn btn-accent btn-sm" style={{ background: 'linear-gradient(135deg, #64748b, #475569)', color: '#ffffff', borderColor: 'transparent', display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }} onClick={() => openAddItemsModal(entry)}>
                              <HiOutlineCube /> Add Items & Verify
                            </button>
                            <button className="btn btn-secondary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }} onClick={() => handleOpenGateVerify(entry._id)}>
                              <HiOutlineCheckCircle /> Verify Only
                            </button>
                          </div>
                        </td>
                      ) : (
                        <>
                          <td>
                            <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{entry.verifiedBy?.name || 'Supervisor'}</div>
                            <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)', marginTop: '2px' }}>
                              ⏱️ {new Date(entry.updatedAt).toLocaleString()}
                            </div>
                          </td>
                          <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                            {entry.remarks || 'Verified without remarks.'}
                          </td>
                          <td>
                            <span className={`azure-badge ${entry.status === 'verified' ? 'running' :
                                entry.status === 'grn_created' ? 'success' : 'danger'
                              }`} style={{ textTransform: 'capitalize' }}>
                              {entry.status === 'verified' ? 'Verified' :
                                entry.status === 'grn_created' ? 'GRN Created' : entry.status}
                            </span>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      ) : activeTab === 'machines' ? (

        /* MACHINE AUTOMATION */
        <div className="azure-card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
            <h3 style={{ color: 'var(--text-primary)', fontSize: '1.1rem', margin: 0 }}>Factory Machinery Status</h3>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flex: 1, justifyContent: 'flex-end', maxWidth: '500px' }}>
              <div style={{ position: 'relative', width: '100%', maxWidth: '340px' }}>
                <input
                  type="text"
                  className="form-input"
                  style={{ padding: '8px 12px 8px 36px', width: '100%' }}
                  placeholder="Search machine by name, line, status..."
                  value={machineSearch}
                  onChange={e => setMachineSearch(e.target.value)}
                />
                <HiOutlineSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)', fontSize: '1.1rem' }} />
              </div>
              {machineSearch && (
                <button className="btn btn-secondary btn-sm" onClick={() => setMachineSearch('')}>Clear</button>
              )}
              {['super_admin', 'admin'].includes(user?.role) && (
                <button className="btn btn-primary btn-sm" onClick={() => {
                  setEditingMachineId(null);
                  setMachineForm({ name: '', status: 'available', remarks: '', capacity: '', modelNumber: '', powerRating: '', lastServiceDate: '', productionLine: 'Line 1' });
                  setSelectedMachineType('');
                  setSelectedCategory('');
                  setSelectedSubMachine('');
                  setShowCustomDropdown(false);
                  setActiveHoverCategory(null);
                  setIsCustomMachineName(false);
                  setShowMachineModal(true);
                }}><HiOutlinePlus /> Add Machine</button>
              )}
            </div>
          </div>

          {machines.length === 0 ? (
            <p style={{ color: 'var(--text-dim)', textAlign: 'center', padding: '20px' }}>No machines registered.</p>
          ) : (() => {
            const filteredMachines = machines.filter(m => {
              if (!machineSearch.trim()) return true;
              const q = machineSearch.toLowerCase();
              return (
                m.name?.toLowerCase().includes(q) ||
                m.status?.toLowerCase().includes(q) ||
                m.productionLine?.toLowerCase().includes(q) ||
                m.modelNumber?.toLowerCase().includes(q) ||
                m.remarks?.toLowerCase().includes(q)
              );
            });

            if (filteredMachines.length === 0) {
              return (
                <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-dim)' }}>
                  No machines matching "{machineSearch}" found.
                </div>
              );
            }

            const grouped = filteredMachines.reduce((acc, m) => {
              const line = m.productionLine || 'Line 1';
              if (!acc[line]) acc[line] = [];
              acc[line].push(m);
              return acc;
            }, {});
            const sortedLines = Object.keys(grouped).sort();


            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {sortedLines.map(lineName => (
                  <div key={lineName} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, 320px)', gap: '20px' }}>
                    {grouped[lineName].map(mach => {
                      const cardClass = ['working', 'available'].includes(mach.status) ? 'success' :
                        ['damaged', 'broken', 'destroyed'].includes(mach.status) ? 'danger' : 'warning';

                      const getBadgeColor = (status) => {
                        switch (status) {
                          case 'working':
                          case 'available':
                            return { bg: 'rgba(16, 185, 129, 0.08)', color: '#10b981', border: '1px solid rgba(16, 185, 129, 0.2)' };
                          case 'damaged':
                          case 'broken':
                          case 'destroyed':
                            return { bg: 'rgba(239, 68, 68, 0.08)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.2)' };
                          case 'not found':
                            return { bg: 'rgba(107, 114, 128, 0.08)', color: '#9ca3af', border: '1px solid rgba(107, 114, 128, 0.2)' };
                          default:
                            return { bg: 'rgba(245, 158, 11, 0.08)', color: '#f59e0b', border: '1px solid rgba(245, 158, 11, 0.2)' };
                        }
                      };
                      const badgeStyle = getBadgeColor(mach.status);

                      return (
                        <div key={mach._id} className={`azure-card ${cardClass}`} style={{ padding: '20px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h4 style={{ color: 'var(--text-primary)', margin: '0 0 8px 0', fontSize: '1.05rem', fontWeight: 700 }}>{mach.name}</h4>
                            <select
                              value={mach.status}
                              onChange={async (e) => {
                                const newStatus = e.target.value;
                                try {
                                  await operationsAPI.updateMachine(mach._id, { status: newStatus });
                                  toast.success(`Machine status updated to ${newStatus}`);
                                  fetchData();
                                } catch (err) {
                                  toast.error('Failed to update machine status');
                                }
                              }}
                              style={{
                                background: badgeStyle.bg,
                                color: badgeStyle.color,
                                border: badgeStyle.border,
                                padding: '4px 10px',
                                borderRadius: '6px',
                                fontSize: '0.75rem',
                                fontWeight: 'bold',
                                textTransform: 'uppercase',
                                cursor: 'pointer',
                                outline: 'none',
                                textAlign: 'center',
                                minWidth: '100px'
                              }}
                            >
                              <option value="working" style={{ background: '#1e293b', color: '#10b981' }}>Working</option>
                              <option value="available" style={{ background: '#1e293b', color: '#10b981' }}>Available</option>
                              <option value="damaged" style={{ background: '#1e293b', color: '#ef4444' }}>Damaged</option>
                              <option value="not found" style={{ background: '#1e293b', color: '#9ca3af' }}>Not Found</option>
                              <option value="idle" style={{ background: '#1e293b', color: '#f59e0b' }}>Idle</option>
                              <option value="destroyed" style={{ background: '#1e293b', color: '#ef4444' }}>Destroyed</option>
                            </select>
                          </div>
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '12px 0', display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: '8px' }}>
                            <div><strong>Line:</strong> <span style={{ color: 'var(--primary)', fontWeight: 'bold' }}>{mach.productionLine || 'Line 1'}</span></div>
                            <div><strong>Cap:</strong> {mach.capacity || '--'}</div>
                            <div style={{ gridColumn: 'span 2' }}><strong>Model:</strong> {mach.modelNumber || '--'}</div>
                            <div><strong>Power:</strong> {mach.powerRating || '--'}</div>
                            <div><strong>Serviced:</strong> {mach.lastServiceDate ? new Date(mach.lastServiceDate).toLocaleDateString() : '--'}</div>
                          </div>
                          <p style={{ fontSize: '0.82rem', color: 'var(--text-dim)', minHeight: '32px', marginBottom: '16px', lineHeight: '1.4' }}>{mach.remarks || 'No remarks.'}</p>
                          {['super_admin', 'admin'].includes(user?.role) && (
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', borderTop: '1px solid rgba(255,255,255,0.04)', paddingTop: '12px' }}>
                              <button className="btn btn-secondary btn-sm" style={{ padding: '4px 12px', color: '#3b82f6', borderColor: '#3b82f6', display: 'inline-flex', alignItems: 'center', gap: '4px' }} onClick={() => handleEditMachine(mach)}>
                                <HiOutlinePencil /> Edit
                              </button>
                              <button className="btn btn-secondary btn-sm" style={{ padding: '4px 12px', color: '#ef4444', borderColor: '#ef4444', display: 'inline-flex', alignItems: 'center', gap: '4px' }} onClick={() => handleDeleteMachine(mach._id)}>
                                <HiOutlineTrash /> Delete
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            );
          })()}
        </div>

      ) : activeTab === 'order_history' ? (

        /* ORDER LIVE & HISTORY VIEW */
        <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>

          {/* LIVE BUILDING QUEUE */}
          <div className="azure-card" style={{ padding: '24px' }}>
            <h3 style={{ color: 'var(--text-primary)', fontSize: '1.1rem', margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              ⚡ Live Building Orders
            </h3>
            {buildJobs.filter(j => ['pending', 'processing', 'shortage_reported', 'delayed'].includes(j.status)).length === 0 ? (
              <p style={{ color: 'var(--text-dim)', textAlign: 'center', padding: '20px' }}>
                No active orders are currently building in real-time.
              </p>
            ) : (
              <div className="azure-table-container">
                <table className="azure-table">
                  <thead>
                    <tr>
                      <th>Product Info</th>
                      <th>Quantity</th>
                      <th>Machine & Line</th>
                      <th>Status</th>
                      <th>Start Date / Time</th>
                      <th>Time Remaining / ETA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {buildJobs.filter(j => ['pending', 'processing', 'shortage_reported', 'delayed'].includes(j.status)).map(job => (
                      <tr key={job._id}>
                        <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                          {job.productName}
                          {job.productSize && <span style={{ fontSize: '0.78rem', color: 'var(--text-dim)', marginLeft: '6px' }}>({job.productSize})</span>}
                        </td>
                        <td style={{ fontWeight: 700 }}>{job.orderQuantity} pcs</td>
                        <td>
                          <span style={{ fontWeight: 500 }}>{job.machineName || 'Not Assigned'}</span>
                          <div style={{ fontSize: '0.75rem', color: 'var(--primary)' }}>📍 {job.productionLine || 'Line 1'}</div>
                          {formatBuildProcessSpecs(job) && <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)', marginTop: '2px' }}>{formatBuildProcessSpecs(job)}</div>}
                        </td>
                        <td>
                          <span className={`azure-badge ${job.status === 'processing' ? 'success' :
                              job.status === 'delayed' ? 'danger' :
                                job.status === 'shortage_reported' ? 'danger' : 'warning'
                            }`}>
                            {job.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td style={{ fontSize: '0.82rem' }}>
                          {new Date(job.createdAt).toLocaleString()}
                        </td>
                        <td>
                          <CountdownTimer job={job} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* COMPLETED HISTORY */}
          <div className="azure-card" style={{ padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
              <h3 style={{ color: 'var(--text-primary)', fontSize: '1.1rem', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                📜 Completed Build History
              </h3>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <div style={{ position: 'relative', width: '280px' }}>
                  <input
                    type="text"
                    className="form-input"
                    style={{ padding: '8px 12px 8px 36px', width: '100%' }}
                    placeholder="Search completed builds..."
                    value={historySearch}
                    onChange={e => setHistorySearch(e.target.value)}
                  />
                  <HiOutlineSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)', fontSize: '1.1rem' }} />
                </div>
                {historySearch && (
                  <button className="btn btn-secondary btn-sm" onClick={() => setHistorySearch('')}>Clear</button>
                )}
              </div>
            </div>

            {(() => {
              const completedJobs = buildJobs.filter(j => ['completed', 'received', 'declined'].includes(j.status));
              const filteredCompleted = completedJobs.filter(j => {
                if (!historySearch.trim()) return true;
                const q = historySearch.toLowerCase();
                return (
                  j.productName?.toLowerCase().includes(q) ||
                  j.machineName?.toLowerCase().includes(q) ||
                  j.productionLine?.toLowerCase().includes(q) ||
                  j.status?.toLowerCase().includes(q) ||
                  j.productSize?.toLowerCase().includes(q)
                );
              });

              if (completedJobs.length === 0) {
                return (
                  <p style={{ color: 'var(--text-dim)', textAlign: 'center', padding: '20px' }}>
                    No completed build logs in history.
                  </p>
                );
              }

              if (filteredCompleted.length === 0) {
                return (
                  <p style={{ color: 'var(--text-dim)', textAlign: 'center', padding: '20px' }}>
                    No completed builds matching "{historySearch}" found.
                  </p>
                );
              }

              return (
                <div>
                  <div className="azure-table-container">
                    <table className="azure-table">
                      <thead>
                        <tr>
                          <th>Product Info</th>
                          <th>Quantity</th>
                          <th>Machine & Line</th>
                          <th>Status</th>
                          <th>Created At</th>
                          <th>Completed At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCompleted.slice((historyPage - 1) * 10, historyPage * 10).map(job => (
                          <tr key={job._id}>
                            <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                              {job.productName}
                              {job.productSize && <span style={{ fontSize: '0.78rem', color: 'var(--text-dim)', marginLeft: '6px' }}>({job.productSize})</span>}
                            </td>
                            <td style={{ fontWeight: 700 }}>{job.orderQuantity} pcs</td>
                            <td>
                              <span>{job.machineName || 'Not Assigned'}</span>
                              <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>📍 {job.productionLine || 'Line 1'}</div>
                              {formatBuildProcessSpecs(job) && <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)', marginTop: '2px' }}>{formatBuildProcessSpecs(job)}</div>}
                            </td>
                            <td>
                              <span className={`azure-badge ${job.status === 'received' ? 'success' :
                                  job.status === 'completed' ? 'success' : 'danger'
                                }`}>
                                {job.status}
                              </span>
                            </td>
                            <td style={{ fontSize: '0.82rem' }}>
                              {new Date(job.createdAt).toLocaleString()}
                            </td>
                            <td style={{ fontSize: '0.82rem', fontWeight: 500, color: 'var(--success)' }}>
                              {job.completedAt ? new Date(job.completedAt).toLocaleString() : new Date(job.updatedAt).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Pagination
                    currentPage={historyPage}
                    totalPages={Math.ceil(filteredCompleted.length / 10) || 1}
                    totalItems={filteredCompleted.length}
                    itemsPerPage={10}
                    onPageChange={setHistoryPage}
                  />
                </div>
              );
            })()}
          </div>


        </div>

      ) : (activeTab === 'build_queue' && user?.role !== 'production_manager') ? (

        /* PRODUCTION BUILD QUEUE */
        <div>
          {/* TODAY'S ACTIVE PRODUCTION LINES & SHOTS */}
          <div className="azure-card" style={{ padding: '24px', marginBottom: '24px', border: '1px solid rgba(249, 115, 22, 0.2)', background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.03), rgba(0, 0, 0, 0.15))' }}>
            <h3 style={{ color: 'var(--text-primary)', fontSize: '1.2rem', margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              ⚡ Today's Active Shots & Production Lines
            </h3>
            {buildJobs.filter(j => ['processing', 'delayed'].includes(j.status)).length === 0 ? (
              <div style={{ padding: '16px', textAlign: 'center', color: 'var(--text-dim)', fontSize: '0.9rem', border: '1px dashed var(--border)', borderRadius: '8px' }}>
                No active shots or production runs currently processing. Add a job below to start.
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
                {buildJobs.filter(j => ['processing', 'delayed'].includes(j.status)).map(job => (
                  <div key={job._id} style={{ padding: '16px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.72rem', background: 'rgba(249, 115, 22, 0.15)', color: 'var(--primary)', padding: '2px 8px', borderRadius: '4px', fontWeight: 'bold', textTransform: 'uppercase' }}>
                        📍 {job.productionLine || 'Line 1'}
                      </span>
                      <span style={{ fontSize: '0.72rem', background: job.status === 'delayed' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(0, 120, 212, 0.15)', color: job.status === 'delayed' ? 'var(--danger)' : '#0078d4', padding: '2px 8px', borderRadius: '4px', fontWeight: 'bold' }}>
                        {job.status === 'delayed' ? '⚠️ Delayed' : '⚡ Running'}
                      </span>
                    </div>
                    <h4 style={{ color: 'var(--text-primary)', margin: 0, fontSize: '0.95rem', fontWeight: 700 }}>{job.productName}</h4>
                    <div style={{ fontSize: '0.8rem', color: 'var(--primary)', fontWeight: 600, marginTop: '2px' }}>
                      👤 Sales: {job.salesPersonName || job.orderedBy?.name || 'Sales Team'}
                    </div>
                    <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                      <strong>Machine:</strong> {job.machineName || 'Unspecified'}
                    </div>
                    {getBuildProcessSpecs(job).map(spec => (
                      <div key={spec.label} style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                        <strong>{spec.label}:</strong> {spec.value}
                      </div>
                    ))}
                    <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', display: 'flex', justifyContent: 'space-between' }}>
                      <span><strong>Qty:</strong> {job.orderQuantity} pcs</span>
                      {job.completedShots > 0 && (
                        <span style={{ color: 'var(--success)', fontWeight: 'bold' }}><strong>Batches/Shots:</strong> {job.completedShots} Runs</span>
                      )}
                    </div>
                    {job.estimatedDate && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)', borderTop: '1px solid rgba(255,255,255,0.04)', paddingTop: '6px', marginTop: '4px' }}>
                        ⏱️ Est. Completion: {new Date(job.estimatedDate).toLocaleString()}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* MAIN BUILD QUEUE */}
          <div className="azure-card" style={{ padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: 'var(--text-primary)', fontSize: '1.1rem', margin: 0 }}>Active Build Queue</h3>
              <button className="btn btn-primary btn-sm" onClick={handleOpenCreateBuild}>
                <HiOutlinePlus /> Create Build Process
              </button>
            </div>
            {groupedBuildJobs.length === 0 ? <p style={{ color: 'var(--text-dim)', textAlign: 'center', padding: '20px' }}>No active production orders in queue.</p> : (
              <div className="azure-table-container">
                <table className="azure-table">
                  <thead>
                    <tr>
                      <th>Product Details</th>
                      <th>Ordered Qty</th>
                      <th>Ordered By</th>
                      <th>Created Date</th>
                      <th>Status</th>
                      <th>Build Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedBuildJobs.map(job => (
                      <tr key={job._id}>
                        <td>
                          <div style={{ fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                            <span>{job.productName}</span>
                            {job.isRework && (
                              <span style={{ fontSize: '0.72rem', background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)', padding: '2px 8px', borderRadius: '4px', fontWeight: 700 }}>
                                ⚠️ QC REWORK ({job.orderQuantity} pcs)
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginTop: '2px' }}>Size: {job.productSize || 'N/A'}</div>
                          {job.description && (
                            <div style={{ fontSize: '0.75rem', color: job.isRework ? '#f59e0b' : 'var(--text-dim)', marginTop: '2px', fontWeight: job.isRework ? 600 : 'normal' }}>
                              {job.description}
                            </div>
                          )}
                          {job.machineId && typeof job.machineId === 'object' ? (
                            <div style={{ fontSize: '0.75rem', color: 'var(--primary)', marginTop: '4px', fontWeight: 600 }}>
                              ⚙️ Machine: {job.machineId.name} | 📍 Line: {job.productionLine || job.machineId.productionLine || 'Line 1'} {job.completedShots ? `| 🔄 Batches/Shots: ${job.completedShots}` : ''}
                            </div>
                          ) : job.machineName ? (
                            <div style={{ fontSize: '0.75rem', color: 'var(--primary)', marginTop: '4px', fontWeight: 600 }}>
                              ⚙️ Machine: {job.machineName} | 📍 Line: {job.productionLine || 'Line 1'} {job.completedShots ? `| 🔄 Batches/Shots: ${job.completedShots}` : ''}
                            </div>
                          ) : null}
                          {formatBuildProcessSpecs(job) && (
                            <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
                              🛠️ {formatBuildProcessSpecs(job)}
                            </div>
                          )}
                          {job.materialsUsed && job.materialsUsed.length > 0 && (
                            <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
                              📦 Materials: {job.materialsUsed.map(m => `${m.quantity} ${m.unit} ${m.materialName}`).join(', ')}
                            </div>
                          )}
                        </td>
                        <td style={{ fontWeight: 700, color: 'var(--success)' }}>
                          <div>{job.orderQuantity} pcs</div>
                          {(() => {
                            let linkedShortage = null;
                            if (job.shortageLogId) {
                              const targetId = (job.shortageLogId._id || job.shortageLogId).toString();
                              linkedShortage = allSbsLogs.find(s => s._id.toString() === targetId);
                            }
                            if (!linkedShortage && job.inventoryItemId) {
                              const invId = (job.inventoryItemId._id || job.inventoryItemId).toString();
                              linkedShortage = allSbsLogs.find(s => 
                                s.inventoryItemId && 
                                (s.inventoryItemId._id || s.inventoryItemId).toString() === invId && 
                                s.type === 'shortage' && 
                                s.status !== 'completed'
                              );
                            }
                            if (linkedShortage) {
                              return (
                                <span style={{ fontSize: '0.72rem', background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)', padding: '2px 6px', borderRadius: '4px', marginTop: '4px', display: 'inline-block', fontWeight: 600 }}>
                                  🚨 {linkedShortage.quantity} {linkedShortage.unit} Shortage
                                </span>
                              );
                            }
                            return null;
                          })()}
                        </td>
                        <td>
                          <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                            👤 {job.orderedByName || job.salesPersonName || job.orderedBy?.name || 'Sales Officer'}
                          </div>
                          <span style={{ fontSize: '0.72rem', background: 'rgba(59,130,246,0.1)', color: 'var(--primary)', padding: '2px 6px', borderRadius: '4px', textTransform: 'uppercase', fontWeight: 600 }}>
                            {job.orderedRole || 'SALES'}
                          </span>
                        </td>
                        <td>
                          <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                            {new Date(job.createdAt).toLocaleDateString()}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginTop: '2px' }}>
                            {new Date(job.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <select
                              value={job.status}
                              disabled={['completed', 'received', 'declined'].includes(job.status)}
                              onChange={(e) => handleStatusChange(job, e.target.value)}
                              style={{
                                padding: '6px 10px',
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                borderRadius: '4px',
                                background: job.status === 'completed' || job.status === 'received' ? 'rgba(16, 124, 65, 0.15)' :
                                  job.status === 'processing' ? 'rgba(0, 120, 212, 0.15)' :
                                    job.status === 'delayed' || job.status === 'shortage_reported' ? 'rgba(168, 0, 0, 0.15)' : 'rgba(255, 185, 0, 0.15)',
                                color: job.status === 'completed' || job.status === 'received' ? '#107c41' :
                                  job.status === 'processing' ? '#0078d4' :
                                    job.status === 'delayed' || job.status === 'shortage_reported' ? '#a80000' : '#d29200',
                                border: '1px solid currentColor',
                                cursor: 'pointer',
                                textTransform: 'capitalize',
                                outline: 'none'
                              }}
                            >
                              <option value="pending" style={{ backgroundColor: '#1e293b', color: '#ffffff' }}>Pending</option>
                              <option value="processing" style={{ backgroundColor: '#1e293b', color: '#ffffff' }}>Processing</option>
                              <option value="delayed" style={{ backgroundColor: '#1e293b', color: '#ffffff' }}>Delayed</option>
                              <option value="shortage_reported" style={{ backgroundColor: '#1e293b', color: '#ffffff' }}>Shortage Reported</option>
                              <option value="completed" style={{ backgroundColor: '#1e293b', color: '#ffffff' }}>Completed</option>
                              <option value="received" disabled={job.status !== 'received'} style={{ backgroundColor: '#1e293b', color: '#ffffff' }}>Received</option>
                              <option value="declined" disabled={job.status !== 'declined'} style={{ backgroundColor: '#1e293b', color: '#ffffff' }}>Declined</option>
                            </select>
                            {job.estimatedDate && (
                              <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)', marginTop: '4px' }}>
                                Est: {new Date(job.estimatedDate).toLocaleString()}
                              </div>
                            )}
                          </div>
                        </td>
                        <td style={{ verticalAlign: 'middle' }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                            {job.status === 'pending' && (
                              <>
                                <button className="btn btn-primary btn-sm" onClick={() => handleOpenBuild(job)}>
                                  <HiOutlinePlay /> Start Build
                                </button>
                                <button className="btn btn-secondary btn-sm" style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }} onClick={() => reportShortage(job)}>
                                  Report Shortage
                                </button>
                              </>
                            )}
                            {job.status === 'shortage_reported' && (
                              <>
                                <button className="btn btn-primary btn-sm" onClick={() => handleOpenBuild(job)}>
                                  <HiOutlinePlay /> Start Build (Override)
                                </button>
                                <button className="btn btn-secondary btn-sm" onClick={() => handleOpenEditBuild(job)}>
                                  Edit Process
                                </button>
                              </>
                            )}
                            {['processing', 'delayed'].includes(job.status) && (
                              <>
                                <button className="btn btn-accent btn-sm" onClick={() => handleOpenCompleteBuild(job)} title="Complete production build and send to Quality Check">
                                  <HiOutlineClipboardCheck /> Complete Build (Send to QC)
                                </button>
                                <button className="btn btn-secondary btn-sm" onClick={() => handleOpenEditBuild(job)}>
                                  Edit Process
                                </button>
                              </>
                            )}
                            {job.status === 'completed' && (!job.qcStatus || job.qcStatus === 'pending_qc') && (
                              <button
                                className="btn btn-warning btn-sm"
                                style={{ background: '#f59e0b', color: '#fff', border: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                onClick={() => { setActiveTab('quality_check'); handleOpenSupervisorQC(job, 'pass'); }}
                                title="Click to conduct Quality Check now"
                              >
                                <HiOutlineClipboardCheck /> Quality Check Pending
                              </button>
                            )}
                            {job.status === 'completed' && job.qcStatus && job.qcStatus !== 'pending_qc' && (
                              <span style={{ color: '#10b981', fontSize: '0.82rem', fontWeight: 600 }}>
                                ✅ QC Inspected ({job.qcStatus})
                              </span>
                            )}
                            {['received', 'declined'].includes(job.status) && (
                              <span style={{ color: 'var(--text-dim)', fontSize: '0.85rem' }}>Finished ({job.status.replace('_', ' ')})</span>
                            )}

                            {/* ADMIN ONLY EDIT & DELETE OPTIONS */}
                            {['admin', 'super_admin'].includes(user?.role) && (
                              <div style={{ display: 'inline-flex', gap: '6px', marginLeft: 'auto' }}>
                                <button
                                  className="btn btn-secondary btn-sm"
                                  style={{ color: '#3b82f6', borderColor: '#3b82f6', padding: '4px 10px', fontSize: '0.78rem', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                  onClick={() => handleOpenEditBuild(job)}
                                  title="Edit Manufacturing Queue Entry (Admin Only)"
                                >
                                  <HiOutlinePencil /> Edit
                                </button>
                                <button
                                  className="btn btn-secondary btn-sm"
                                  style={{ color: '#ef4444', borderColor: '#ef4444', padding: '4px 10px', fontSize: '0.78rem', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                  onClick={() => handleDeleteBuildJob(job._id)}
                                  title="Delete Manufacturing Queue Entry (Admin Only)"
                                >
                                  <HiOutlineTrash /> Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : (activeTab === 'tasks' && user?.role !== 'production_manager') ? (

        /* ASSIGNED TASKS & SHORTAGES */
        <div className="azure-card" style={{ padding: '24px' }}>
          <h3 style={{ color: 'var(--text-primary)', fontSize: '1.1rem', marginBottom: '20px' }}>
            Assigned Shortages & Tasks Log
          </h3>
          {shortages.length === 0 && allInventoryItems.filter(item => item.productionStatus === 'assigned_to_supervisor').length === 0 ? (
            <p style={{ color: 'var(--text-dim)', textAlign: 'center', padding: '20px' }}>
              No tasks or shortages assigned to your role currently.
            </p>
          ) : (
            <div className="azure-table-container">
              <table className="azure-table">
                <thead>
                  <tr>
                    <th>Item Details</th>
                    <th>Type</th>
                    <th>Shortage Deficit (To Build)</th>
                    <th>Status</th>
                    <th>Date Assigned</th>
                    <th>View Product</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {/* 1. ShortageBuySale items */}
                  {shortages.map(item => (
                    <tr key={item._id}>
                      <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                        <div>{item.itemName}</div>
                        {item.remarks && (
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginTop: '4px', fontWeight: 'normal' }}>
                            📝 {item.remarks}
                          </div>
                        )}
                      </td>
                      <td>
                        <span style={{ fontSize: '0.8rem', background: 'rgba(255,255,255,0.04)', padding: '4px 8px', borderRadius: '4px', textTransform: 'uppercase' }}>
                          {item.type}
                        </span>
                      </td>
                      <td style={{ fontWeight: 700, color: 'var(--danger)' }}>
                        <div style={{ fontSize: '1rem', color: '#ef4444' }}>{item.quantity} {item.unit}</div>
                        <span style={{ fontSize: '0.7rem', color: 'var(--text-dim)', fontWeight: 'normal', textTransform: 'uppercase' }}>
                          Shortage Deficit
                        </span>
                      </td>
                      <td>
                        <span className={`azure-badge ${item.status === 'completed' ? 'success' :
                            item.status === 'assigned' ? 'accent' : 'warning'
                          }`}>
                          {item.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td>{new Date(item.updatedAt).toLocaleDateString()}</td>
                      <td>
                        <button
                          className="btn btn-secondary btn-sm"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '6px 12px', fontSize: '0.8rem' }}
                          onClick={() => handleOpenViewSpecs(item)}
                        >
                          <HiOutlineEye style={{ fontSize: '0.9rem' }} /> View
                        </button>
                      </td>
                      <td style={{ verticalAlign: 'middle' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                          {item.status !== 'completed' ? (() => {
                            const linkedJob = buildJobs.find(j => j.shortageLogId === item._id || (j._id && j._id.toString() === (item.shortageLogId || '').toString()));
                            
                            if (linkedJob && ['processing', 'delayed'].includes(linkedJob.status)) {
                              return (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                  <CountdownTimer job={linkedJob} onStatusChange={handleStatusChange} />
                                </div>
                              );
                            }
                            
                            if (linkedJob && ['completed', 'received'].includes(linkedJob.status)) {
                              return (
                                <span style={{ color: 'var(--success)', fontSize: '0.85rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                  ✓ Completed
                                </span>
                              );
                            }

                            return (
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                  className="btn btn-primary btn-sm"
                                  style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '6px 12px', background: '#10b981', borderColor: '#10b981', color: '#fff', fontWeight: 600 }}
                                  onClick={() => handleOpenBuildFromShortage(item)}
                                >
                                  <HiOutlinePlay /> Order Build
                                </button>
                                <button
                                  className="btn btn-secondary btn-sm"
                                  style={{ color: 'var(--primary)', borderColor: 'var(--primary)', display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '6px 12px' }}
                                  onClick={async () => {
                                    if (window.confirm(`Are you sure you want to forward this shortage task to Sales?`)) {
                                      try {
                                        await operationsAPI.updateShortageBuySale(item._id, { assignedTo: 'sales', status: 'pending' });
                                        toast.success('Task successfully forwarded to Sales!');
                                        fetchData();
                                      } catch (err) {
                                        toast.error('Failed to forward task to Sales');
                                      }
                                    }
                                  }}
                                >
                                  <HiOutlineArrowCircleRight /> Forward to Sales
                                </button>
                              </div>
                            );
                          })() : (
                            <span style={{ color: 'var(--success)', fontSize: '0.85rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                              ✓ Completed
                            </span>
                          )}

                          {/* ADMIN & SUPERADMIN EDIT & DELETE BUTTONS */}
                          {['admin', 'super_admin'].includes(user?.role) && (
                            <div style={{ display: 'inline-flex', gap: '6px', marginLeft: 'auto' }}>
                              <button
                                className="btn btn-secondary btn-sm"
                                style={{ color: '#3b82f6', borderColor: '#3b82f6', padding: '4px 10px', fontSize: '0.78rem', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                onClick={() => handleOpenEditShortageLog(item)}
                                title="Edit Shortage Log (Admin Only)"
                              >
                                <HiOutlinePencil /> Edit
                              </button>
                              <button
                                className="btn btn-secondary btn-sm"
                                style={{ color: '#ef4444', borderColor: '#ef4444', padding: '4px 10px', fontSize: '0.78rem', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                onClick={() => handleDeleteShortageLog(item._id)}
                                title="Delete Shortage Log (Admin Only)"
                              >
                                <HiOutlineTrash /> Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}

                  {/* 2. Unified Inventory Shortage items assigned to Supervisor */}
                  {allInventoryItems.filter(item => item.productionStatus === 'assigned_to_supervisor').map(item => (
                    <tr key={item._id}>
                      <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                        <div>{item.name}</div>
                        {item.storeRemarks && (
                          <div style={{ fontSize: '0.75rem', color: '#22c55e', marginTop: '2px' }}>
                            ✅ Store Remark: {item.storeRemarks}
                          </div>
                        )}
                        {item.pmRemarks && (
                          <div style={{ fontSize: '0.75rem', color: 'var(--accent)', marginTop: '2px', fontWeight: 600 }}>
                            🚨 PM Remark: {item.pmRemarks}
                          </div>
                        )}
                      </td>
                      <td>
                        <span className="azure-badge danger">
                          INVENTORY SHORTAGE
                        </span>
                      </td>
                      <td style={{ fontWeight: 700, color: 'var(--danger)' }}>
                        {getRequiredQty(item)} {item.unit || 'pcs'}
                      </td>
                      <td>
                        <span className="azure-badge running">
                          ASSIGNED TO YOU
                        </span>
                      </td>
                      <td>{item.assignedSupervisorAt ? new Date(item.assignedSupervisorAt).toLocaleDateString() : new Date(item.updatedAt).toLocaleDateString()}</td>
                      <td>
                        <button
                          className="btn btn-secondary btn-sm"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '6px 12px', fontSize: '0.8rem' }}
                          onClick={() => handleOpenViewSpecs(item)}
                        >
                          <HiOutlineEye style={{ fontSize: '0.9rem' }} /> View
                        </button>
                      </td>
                      <td style={{ verticalAlign: 'middle' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                          {(() => {
                            const linkedJob = buildJobs.find(j => j.shortageLogId === item._id);
                            if (linkedJob) {
                              return (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                  <CountdownTimer job={linkedJob} onStatusChange={handleStatusChange} />
                                  <button
                                    className="btn btn-secondary btn-sm"
                                    style={{ color: '#3b82f6', borderColor: '#3b82f6', display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '6px 12px' }}
                                    onClick={() => handleOpenEditBuild(linkedJob)}
                                  >
                                    <HiOutlineCog /> Edit
                                  </button>
                                </div>
                              );
                            }
                            return (
                              <button
                                className="btn btn-primary btn-sm"
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '6px 12px', background: '#10b981', borderColor: '#10b981', color: '#fff', fontWeight: 600 }}
                                onClick={() => handleOpenBuildFromShortage(item)}
                              >
                                <HiOutlinePlay /> Order Build
                              </button>
                            );
                          })()}

                          {/* ADMIN & SUPERADMIN EDIT & DELETE BUTTONS */}
                          {['admin', 'super_admin'].includes(user?.role) && (
                            <div style={{ display: 'inline-flex', gap: '6px', marginLeft: 'auto' }}>
                              <button
                                className="btn btn-secondary btn-sm"
                                style={{ color: '#3b82f6', borderColor: '#3b82f6', padding: '4px 10px', fontSize: '0.78rem', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                onClick={() => handleEditMaterial(item)}
                                title="Edit Inventory Item (Admin Only)"
                              >
                                <HiOutlinePencil /> Edit
                              </button>
                              <button
                                className="btn btn-secondary btn-sm"
                                style={{ color: '#ef4444', borderColor: '#ef4444', padding: '4px 10px', fontSize: '0.78rem', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                onClick={() => handleDeleteMaterialItem(item._id)}
                                title="Delete Inventory Item (Admin Only)"
                              >
                                <HiOutlineTrash /> Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : activeTab === 'materials' ? (

        /* MATERIAL INVENTORY TAB */
        <div className="azure-card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <h3 style={{ color: 'var(--text-primary)', fontSize: '1.1rem', margin: 0 }}>Material Inventory Registry</h3>
              <input
                type="text"
                className="form-input"
                style={{ padding: '6px 12px', fontSize: '0.85rem', width: '260px', margin: 0 }}
                placeholder="🔍 Search materials by name or size..."
                value={materialSearch}
                onChange={e => setMaterialSearch(e.target.value)}
              />
            </div>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => {
                setEditingMaterialId(null);
                setMaterialForm({ name: '', type: 'raw_material', quantity: 0, unit: 'kg', location: 'Production Area', size: '', description: '' });
                setShowMaterialModal(true);
              }}
            >
              <HiOutlinePlus /> Add Material
            </button>
          </div>

          <div className="azure-table-container">
            <table className="azure-table">
              <thead>
                <tr>
                  <th>Material Name</th>
                  <th>Available Qty</th>
                  <th>Required Qty</th>
                  <th>Size Specification</th>
                  <th>Location</th>
                  <th>Added Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rawMaterials.filter(item => {
                  const term = materialSearch.toLowerCase();
                  return (
                    item.name.toLowerCase().includes(term) ||
                    (item.size && item.size.toLowerCase().includes(term)) ||
                    (item.location && item.location.toLowerCase().includes(term)) ||
                    (item.description && item.description.toLowerCase().includes(term))
                  );
                }).length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: '30px', color: 'var(--text-dim)' }}>
                      No matching materials found.
                    </td>
                  </tr>
                ) : (
                  rawMaterials.filter(item => {
                    const term = materialSearch.toLowerCase();
                    return (
                      item.name.toLowerCase().includes(term) ||
                      (item.size && item.size.toLowerCase().includes(term)) ||
                      (item.location && item.location.toLowerCase().includes(term)) ||
                      (item.description && item.description.toLowerCase().includes(term))
                    );
                  }).map(item => {
                    const reqQty = getRequiredQty(item);
                    return (
                      <tr key={item._id}>
                        <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                          {item.name}
                          {item.description && (
                            <div style={{ fontSize: '0.78rem', color: 'var(--text-dim)', fontWeight: 'normal', marginTop: '2px' }}>
                              {item.description}
                            </div>
                          )}
                        </td>
                        <td style={{ fontWeight: 700, color: (item.quantity - reqQty) <= 0 ? 'var(--danger)' : 'var(--success)' }}>
                          {Math.max(0, item.quantity - reqQty)} {item.unit}
                        </td>
                        <td style={{ fontWeight: 700, color: '#ef4444' }}>
                          {reqQty} {item.unit}
                        </td>
                        <td style={{ fontSize: '0.85rem' }}>{item.size || 'N/A'}</td>
                        <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{item.location || 'Unassigned'}</td>
                        <td style={{ fontSize: '0.85rem' }}>
                          {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : 'N/A'}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '12px' }}>
                            <button
                              style={{ color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                              onClick={() => handleEditMaterial(item)}
                            >
                              <HiOutlinePencil /> Edit
                            </button>
                            <button
                              style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                              onClick={() => handleDeleteMaterialItem(item._id)}
                            >
                              <HiOutlineTrash /> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : activeTab === 'order_shortages' ? (
        <div className="azure-card" style={{ padding: '24px' }}>
          <h3 style={{ marginBottom: '20px', color: 'var(--text-primary)', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
            ⚠️ Client Order Shortages (Difference Reports)
          </h3>
          {orderShortages.length === 0 ? (
            <p style={{ color: 'var(--text-dim)', textAlign: 'center', padding: '20px' }}>No active client order shortages.</p>
          ) : (
            <div className="azure-table-container">
              <table className="azure-table">
                <thead>
                  <tr>
                    <th>Ref Order</th>
                    <th>Model Number</th>
                    <th>Shortage Qty</th>
                    <th>Status</th>
                    <th>Planned Shots</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {orderShortages.map(s => (
                    <tr key={s._id}>
                      <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{s.orderId?.orderNumber || 'N/A'}</td>
                      <td style={{ fontWeight: 600 }}>{s.modelNumber}</td>
                      <td style={{ fontWeight: 700, color: 'var(--danger)' }}>{s.shortageMeters}m</td>
                      <td>
                        <span className={`azure-badge ${s.status === 'pending_planning' ? 'danger' : 'success'}`}>
                          {s.status.replace('_', ' ').toUpperCase()}
                        </span>
                      </td>
                      <td>{s.plannedShots > 0 ? `${s.plannedShots} shots` : 'Not Planned'}</td>
                      <td>
                        {s.status === 'pending_planning' ? (
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => {
                              setSelectedShortage(s);
                              setPlanForm({ plannedShots: '' });
                              setShowPlanModal(true);
                            }}
                          >
                            Plan Production
                          </button>
                        ) : (
                          <span style={{ color: 'var(--success)', fontWeight: 600 }}>Planned</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : activeTab === 'inventory_shortage' ? (
        <div className="azure-card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
            <h3 style={{ color: 'var(--text-primary)', fontSize: '1.1rem', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              📦 Unified Inventory Shortage Registry (PM / Supervisor Workspace)
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>Filter Orders:</label>
              <select
                className="form-input"
                style={{ padding: '6px 12px', fontSize: '0.85rem', cursor: 'pointer', margin: 0, width: '180px' }}
                value={inventoryStatusFilter}
                onChange={e => {
                  setInventoryStatusFilter(e.target.value);
                  setInventoryPage(1);
                }}
              >
                <option value="all">All Orders</option>
                <option value="pending">Pending Orders</option>
                <option value="completed">Completed Orders</option>
              </select>
            </div>
          </div>
          {(() => {
            const filteredItems = allInventoryItems
              .filter(item => item.productionStatus && item.productionStatus !== 'none')
              .filter(item => {
                const linkedJob = buildJobs.find(j => j.shortageLogId === item._id);
                const isDone = item.productionStatus === 'completed' || (linkedJob && ['completed', 'received'].includes(linkedJob.status));
                if (inventoryStatusFilter === 'pending') return !isDone;
                if (inventoryStatusFilter === 'completed') return isDone;
                return true;
              });

            const paginatedItems = filteredItems.slice((inventoryPage - 1) * 10, inventoryPage * 10);

            if (filteredItems.length === 0) {
              return <p style={{ color: 'var(--text-dim)', textAlign: 'center', padding: '20px' }}>No inventory shortage orders found for this filter.</p>;
            }

            return (
              <>
                <div className="azure-table-container" style={{ overflowX: 'auto', width: '100%', WebkitOverflowScrolling: 'touch' }}>
                  <table className="azure-table" style={{ width: '100%', minWidth: '1500px', whiteSpace: 'nowrap' }}>
                    <thead>
                      <tr>
                        <th>Item Name</th>
                        <th>Item Type</th>
                        <th>Model Number</th>
                        <th>Inner Size</th>
                        <th>Outer Size</th>
                        {isDragChain && (
                          <>
                            <th>Bending Radius</th>
                            <th>VS</th>
                            <th>HS</th>
                            <th>SR</th>
                          </>
                        )}
                        <th>Available Qty</th>
                        <th>Store Manager Remark</th>
                        <th>Status / Assignee</th>
                        <th>PM Remarks</th>
                        <th>ORDER STATUS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedItems.map(item => {
                        const linkedJob = buildJobs.find(j => j.shortageLogId === item._id);
                        const isDone = item.productionStatus === 'completed' || (linkedJob && ['completed', 'received'].includes(linkedJob.status));

                        return (
                          <tr key={item._id}>
                            <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{item.name}</td>
                            <td style={{ fontSize: '0.85rem' }}>{item.type ? item.type.replace('_', ' ') : 'raw_material'}</td>
                            <td style={{ fontWeight: 600, fontSize: '0.85rem' }}>{item.modelNumber || 'N/A'}</td>
                            <td style={{ fontSize: '0.85rem' }}>{item.size || 'N/A'}</td>
                            <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{item.description?.replace('Outer: ', '') || 'N/A'}</td>
                            {isDragChain && (
                              <>
                                <td style={{ fontSize: '0.85rem' }}>{item.radius || 'N/A'}</td>
                                <td style={{ fontSize: '0.85rem' }}>{item.vs || 'N/A'}</td>
                                <td style={{ fontSize: '0.85rem' }}>{item.hs || 'N/A'}</td>
                                <td style={{ fontSize: '0.85rem' }}>{item.sr || 'N/A'}</td>
                              </>
                            )}
                            <td style={{ fontWeight: 700, color: item.quantity === 0 ? 'var(--danger)' : 'var(--success)' }}>
                              {item.quantity} {item.unit}
                            </td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#22c55e', fontSize: '0.85rem' }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '22px', height: '22px', borderRadius: '50%', background: 'rgba(34, 197, 94, 0.15)', border: '1px solid #22c55e' }}>
                                  <HiOutlineCheckCircle style={{ fontSize: '0.9rem', color: '#22c55e' }} />
                                </span>
                                <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{item.storeRemarks || 'Sent to PM'}</span>
                              </div>
                            </td>
                            <td>
                              {item.productionStatus === 'sent_to_pm' ? (
                                <span className="azure-badge warning">Pending PM Assignment</span>
                              ) : item.productionStatus === 'assigned_to_supervisor' ? (
                                <span className="azure-badge running">Assigned: {item.assignedSupervisor}</span>
                              ) : (
                                <span className="azure-badge success">Completed</span>
                              )}
                            </td>
                            <td style={{ fontSize: '0.85rem', color: 'var(--primary-light)', fontWeight: 500 }}>
                              {item.pmRemarks || 'N/A'}
                            </td>
                            <td>
                              {isDone ? (
                                <span className="azure-badge success" style={{ fontWeight: 700 }}>Done ✅</span>
                              ) : ['production_manager', 'admin', 'super_admin'].includes(user?.role) && item.productionStatus === 'sent_to_pm' ? (
                                <button
                                  className="btn btn-primary btn-sm"
                                  onClick={() => {
                                    setSelectedInventoryItem(item);
                                    setSelectedSupervisor(item.assignedSupervisor || 'Supervisor Line 1');
                                    setPmAssignRemarks(item.pmRemarks || 'Urgent basis priority production required');
                                    setShowAssignSupervisorModal(true);
                                  }}
                                >
                                  Assign to Supervisor
                                </button>
                              ) : (
                                <span className="azure-badge warning" style={{ fontWeight: 700 }}>Pending</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  currentPage={inventoryPage}
                  totalPages={Math.ceil(filteredItems.length / 10) || 1}
                  totalItems={filteredItems.length}
                  itemsPerPage={10}
                  onPageChange={setInventoryPage}
                />
              </>
            );
          })()}
        </div>
      ) : activeTab === 'quality_check' ? (
        /* QUALITY CHECK VIEW FOR SUPERVISOR / PRODUCTION */
        <div className="azure-card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px', minWidth: 0 }}>
            <div style={{ minWidth: 0 }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-primary)' }}>
                <HiOutlineClipboardCheck style={{ color: 'var(--primary)', fontSize: '1.4rem', flexShrink: 0 }} />
                Real-Time Completed Builds - Quality Check
              </h2>
              <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: 'var(--text-dim)' }}>
                Inspect completed builds, approve passed units to Unified Inventory (Store & Sales), and auto-assign rework jobs.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', minWidth: 0, maxWidth: '100%' }}>
              <div style={{ position: 'relative', minWidth: '200px', flex: '0 1 220px' }}>
                <HiOutlineSearch style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)' }} />
                <input
                  type="text"
                  className="form-input"
                  placeholder="Search model, item..."
                  value={supervisorQcSearch}
                  onChange={(e) => {
                    setSupervisorQcSearch(e.target.value);
                    setSupervisorQcPage(1);
                  }}
                  style={{ paddingLeft: '32px', height: '36px', fontSize: '0.85rem' }}
                />
              </div>
              {(() => {
                // Every distinct model number that has at least one completed build
                const allModels = Array.from(new Set(
                  buildJobs
                    .filter(j => j.status === 'completed')
                    .map(j => (j.modelNumber || j.productModel || j.productName || '').trim())
                    .filter(Boolean)
                )).sort((a, b) => a.localeCompare(b));

                // The search box narrows down the dropdown options
                const q = supervisorQcSearch.trim().toLowerCase();
                const visibleModels = q ? allModels.filter(m => m.toLowerCase().includes(q)) : allModels;

                return (
                  <select
                    className="form-input"
                    title={supervisorQcModel || 'Select a model number'}
                    style={{
                      height: '36px',
                      // must override .form-input's 14px vertical padding, otherwise
                      // border-box leaves only 8px of content height and clips the text
                      padding: '8px 12px',
                      lineHeight: '20px',
                      fontSize: '0.85rem',
                      fontWeight: 700,
                      width: 'auto',
                      minWidth: '240px',
                      maxWidth: '420px',
                      textOverflow: 'ellipsis',
                      borderColor: supervisorQcModel ? 'var(--primary)' : 'var(--border)'
                    }}
                    value={supervisorQcModel}
                    onChange={(e) => {
                      setSupervisorQcModel(e.target.value);
                      setSupervisorQcPage(1);
                    }}
                  >
                    <option value="">-- Select Model Number --</option>
                    {visibleModels.map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                );
              })()}

              {supervisorQcModel && (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{ height: '36px', fontWeight: 700 }}
                  onClick={() => { setSupervisorQcModel(''); setSupervisorQcPage(1); }}
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Blank by default - nothing is shown until a model number is picked */}
          {!supervisorQcModel ? (
            <div style={{ textAlign: 'center', padding: '70px 20px', color: 'var(--text-dim)' }}>
              <HiOutlineClipboardCheck style={{ fontSize: '3rem', marginBottom: '12px', opacity: 0.4 }} />
              <div style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '6px' }}>
                Select a Model Number
              </div>
              <p style={{ margin: 0, fontSize: '0.88rem' }}>
                Choose a model number from the dropdown above to see its complete details &mdash;
                build timings, duration, quality check results and full specification.
              </p>
            </div>
          ) : (() => {
            // All completed builds belonging to the selected model
            const modelJobs = buildJobs
              .filter(j => j.status === 'completed')
              .filter(j => {
                const model = (j.modelNumber || j.productModel || j.productName || '').trim();
                return model === supervisorQcModel;
              })
              .sort((a, b) => new Date(b.completedAt || b.createdAt) - new Date(a.completedAt || a.createdAt));

            if (modelJobs.length === 0) {
              return (
                <div style={{ textAlign: 'center', padding: '50px 20px', color: 'var(--text-dim)' }}>
                  <HiOutlineClipboardCheck style={{ fontSize: '2.5rem', marginBottom: '8px', opacity: 0.5 }} />
                  <p>No completed builds found for model <strong>{supervisorQcModel}</strong>.</p>
                </div>
              );
            }

            // Specification is taken from the most recent build of this model
            const spec = modelJobs[0];

            // Aggregate totals across every build of this model
            const totals = modelJobs.reduce((acc, j) => {
              const pending = !j.qcStatus || j.qcStatus === 'pending_qc';
              acc.builds += 1;
              acc.builtQty += Number(j.orderQuantity) || 0;
              acc.passedQty += Number(j.qcPassedQty) || 0;
              acc.failedQty += Number(j.qcFailedQty) || 0;
              if (pending) acc.pending += 1;
              if (j.isRework) acc.rework += 1;
              return acc;
            }, { builds: 0, builtQty: 0, passedQty: 0, failedQty: 0, pending: 0, rework: 0 });

            // Total time spent across all builds of this model
            const firstStart = modelJobs
              .map(j => qcBuildStart(j))
              .filter(Boolean)
              .sort((a, b) => new Date(a) - new Date(b))[0];
            const lastCompletion = modelJobs
              .map(j => j.completedAt)
              .filter(Boolean)
              .sort((a, b) => new Date(b) - new Date(a))[0];

            const paginatedJobs = modelJobs.slice((supervisorQcPage - 1) * 5, supervisorQcPage * 5);

            return (
              <>
                {/* ============ A. MODEL SPECIFICATION ============ */}
                <div style={{
                  background: 'rgba(249, 115, 22, 0.05)',
                  border: '1px solid rgba(249, 115, 22, 0.25)',
                  borderRadius: '12px',
                  padding: '18px 20px',
                  marginBottom: '20px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px', flexWrap: 'wrap', minWidth: 0 }}>
                    <span className="azure-badge primary" style={{
                      fontWeight: 800, fontSize: '0.95rem', padding: '6px 14px',
                      // .azure-badge is inline-flex, so long model codes would not wrap
                      maxWidth: '100%', whiteSpace: 'normal', wordBreak: 'break-word',
                      lineHeight: 1.35, textAlign: 'left'
                    }}>
                      {supervisorQcModel}
                    </span>
                    <span style={{
                      fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)',
                      minWidth: 0, flex: '1 1 240px', wordBreak: 'break-word'
                    }}>
                      {spec.productName || 'Drag Chain'}
                    </span>
                    {totals.pending > 0 && (
                      <span className="azure-badge warning" style={{ fontWeight: 700 }}>
                        {totals.pending} build{totals.pending > 1 ? 's' : ''} awaiting QC
                      </span>
                    )}
                  </div>

                  <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.5px', marginBottom: '10px' }}>
                    Model Specification
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px' }}>
                    <QcInfoTile label="Model Number" value={spec.modelNumber || supervisorQcModel} color="var(--primary)" />
                    <QcInfoTile label="Size / Width" value={spec.productSize} />
                    <QcInfoTile label="Radius" value={spec.radius} />
                    <QcInfoTile label="Design / Chain Type" value={spec.designType} />
                    <QcInfoTile label="Series" value={spec.series} />
                    <QcInfoTile label="VS" value={spec.vs} />
                    <QcInfoTile label="HS" value={spec.hs} />
                    <QcInfoTile label="SR" value={spec.sr} />
                    <QcInfoTile label="Chain Length" value={spec.chainLength ? spec.chainLength + ' mm' : null} />
                    <QcInfoTile label="Unit" value={spec.unit || 'pcs'} />
                    <QcInfoTile label="Item Type" value={spec.itemType} />
                    <QcInfoTile label="Production Line" value={spec.productionLine} />
                  </div>

                  {spec.description && (
                    <div style={{ marginTop: '12px', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                      <strong style={{ color: 'var(--text-dim)' }}>Description:</strong> {spec.description}
                    </div>
                  )}
                </div>

                {/* ============ B. OVERALL SUMMARY FOR THIS MODEL ============ */}
                <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.5px', marginBottom: '10px' }}>
                  Overall Summary &mdash; {supervisorQcModel}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '22px' }}>
                  <QcInfoTile label="Total Builds" value={totals.builds} color="var(--primary)" />
                  <QcInfoTile label="Total Built Qty" value={totals.builtQty + ' ' + (spec.unit || 'pcs')} color="#22c55e" />
                  <QcInfoTile label="Total Passed" value={totals.passedQty + ' ' + (spec.unit || 'pcs')} color="#10b981" />
                  <QcInfoTile label="Total Failed" value={totals.failedQty + ' ' + (spec.unit || 'pcs')} color="#ef4444" />
                  <QcInfoTile label="Pending QC" value={totals.pending} color="#f59e0b" />
                  <QcInfoTile label="Rework Builds" value={totals.rework} color="#a855f7" />
                  <QcInfoTile label="First Build Started" value={qcFormatDateTime(firstStart)} mono />
                  <QcInfoTile label="Last Build Completed" value={qcFormatDateTime(lastCompletion)} mono />
                </div>

                {/* ============ C. BUILD-BY-BUILD DETAIL + QC ACTION ============ */}
                <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.5px', marginBottom: '10px' }}>
                  Build History &amp; Quality Check ({modelJobs.length})
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  {paginatedJobs.map(job => {
                    const isPending = !job.qcStatus || job.qcStatus === 'pending_qc';
                    const startedAt = qcBuildStart(job);
                    const duration = qcFormatDuration(startedAt, job.completedAt);

                    const qcColor = isPending ? '#f59e0b'
                      : (job.qcStatus === 'passed' || job.qcStatus === 'sent_to_store') ? '#10b981'
                      : job.qcStatus === 'failed' ? '#ef4444' : '#a855f7';

                    return (
                      <div key={job._id} style={{
                        border: '1px solid var(--border)',
                        borderLeft: '4px solid ' + qcColor,
                        borderRadius: '10px',
                        padding: '16px 18px',
                        background: 'var(--bg-secondary)'
                      }}>
                        {/* Card header: build id + QC status */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '14px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 800, color: 'var(--primary-light)', fontSize: '0.95rem' }}>
                              #{job.customBuildId || job.orderId?.orderNumber || job._id.slice(-6).toUpperCase()}
                            </span>
                            {job.isRework && (
                              <span className="azure-badge warning" style={{ fontSize: '0.7rem', fontWeight: 700 }}>REWORK</span>
                            )}
                            <span style={{ fontSize: '0.82rem', color: 'var(--text-dim)' }}>
                              {job.orderQuantity || 0} {job.unit || 'pcs'} built
                            </span>
                          </div>

                          <div>
                            {isPending ? (
                              <span className="azure-badge warning" style={{ fontWeight: 700 }}>Pending QC</span>
                            ) : (job.qcStatus === 'passed' || job.qcStatus === 'sent_to_store') ? (
                              <span className="azure-badge success" style={{ fontWeight: 700 }}>
                                Passed ({job.qcPassedQty || 0} {job.unit || 'pcs'})
                              </span>
                            ) : job.qcStatus === 'failed' ? (
                              <span className="azure-badge danger" style={{ fontWeight: 700 }}>
                                Failed ({job.qcFailedQty || 0} {job.unit || 'pcs'})
                              </span>
                            ) : (
                              <span className="azure-badge purple" style={{ fontWeight: 700 }}>
                                Partial &mdash; {job.qcPassedQty || 0} pass / {job.qcFailedQty || 0} fail
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Timings */}
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.4px', marginBottom: '8px' }}>
                          Timings
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', marginBottom: '14px' }}>
                          <QcInfoTile label="Starting Time" value={qcFormatDateTime(startedAt)} mono />
                          <QcInfoTile label="Completion Time" value={qcFormatDateTime(job.completedAt)} mono />
                          <QcInfoTile label="Total Build Duration" value={duration} color="#22c55e" />
                          <QcInfoTile label="Estimated Date" value={qcFormatDateTime(job.estimatedDate)} mono />
                        </div>

                        {/* Production details */}
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.4px', marginBottom: '8px' }}>
                          Production Details
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', marginBottom: '14px' }}>
                          <QcInfoTile label="Machine" value={job.machineName || job.machineId?.name || 'Unassigned'} />
                          <QcInfoTile label="Production Line" value={job.productionLine || 'Line 1'} />
                          <QcInfoTile
                            label="Batches / Shots Completed"
                            value={job.completedShots || job.shotCount || '--'}
                            color="#22c55e"
                          />
                          <QcInfoTile label="Ordered By" value={job.salesPersonName || job.orderedBy?.name} />
                          <QcInfoTile label="Ordered Role" value={job.orderedRole} />
                          <QcInfoTile label="Build Created" value={qcFormatDateTime(job.createdAt)} mono />
                          {getBuildProcessSpecs(job).map(spec => (
                            <QcInfoTile key={spec.label} label={spec.label} value={spec.value} />
                          ))}
                        </div>

                        {Array.isArray(job.materialsUsed) && job.materialsUsed.length > 0 && (
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '10px' }}>
                            <strong style={{ color: 'var(--text-dim)' }}>Materials Used:</strong>{' '}
                            {job.materialsUsed.map(m => `${m.quantity} ${m.unit} ${m.materialName}`).join(', ')}
                          </div>
                        )}

                        {job.completionRemarks && (
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '14px' }}>
                            <strong style={{ color: 'var(--text-dim)' }}>Completion Note:</strong> {job.completionRemarks}
                          </div>
                        )}

                        {Array.isArray(job.customFields) && job.customFields.length > 0 && (
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '14px' }}>
                            <strong style={{ color: 'var(--text-dim)' }}>Build Specs:</strong>{' '}
                            {job.customFields.map(f => `${f.key}: ${f.value}`).join(' | ')}
                          </div>
                        )}

                        {/* Quality check result - shown right here after QC is done */}
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.4px', marginBottom: '8px' }}>
                          Quality Check
                        </div>

                        {isPending ? (
                          <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            flexWrap: 'wrap', gap: '12px',
                            background: 'rgba(245, 158, 11, 0.08)',
                            border: '1px solid rgba(245, 158, 11, 0.25)',
                            borderRadius: '8px', padding: '12px 14px'
                          }}>
                            <span style={{ fontSize: '0.85rem', color: '#f59e0b', fontWeight: 600 }}>
                              Awaiting inspection &mdash; record the result below.
                            </span>
                            <div style={{ display: 'inline-flex', gap: '8px', flexWrap: 'wrap' }}>
                              <button
                                className="btn btn-sm"
                                style={{
                                  background: 'rgba(16, 185, 129, 0.15)', color: '#10b981',
                                  border: '1px solid #10b981', padding: '5px 14px', fontWeight: 700,
                                  fontSize: '0.82rem', display: 'inline-flex', alignItems: 'center', gap: '4px'
                                }}
                                onClick={() => handleOpenSupervisorQC(job, 'pass')}
                                title="Pass all units and send to Unified Inventory"
                              >
                                <HiOutlineCheck /> Pass
                              </button>
                              <button
                                className="btn btn-sm"
                                style={{
                                  background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444',
                                  border: '1px solid #ef4444', padding: '5px 14px', fontWeight: 700,
                                  fontSize: '0.82rem', display: 'inline-flex', alignItems: 'center', gap: '4px'
                                }}
                                onClick={() => handleOpenSupervisorQC(job, 'fail')}
                                title="Fail all units and auto-assign rebuild to supervisor"
                              >
                                <HiOutlineX /> Fail
                              </button>
                              <button
                                className="btn btn-sm"
                                style={{
                                  background: 'rgba(168, 85, 247, 0.15)', color: '#a855f7',
                                  border: '1px solid #a855f7', padding: '5px 14px', fontWeight: 700,
                                  fontSize: '0.82rem'
                                }}
                                onClick={() => handleOpenSupervisorQC(job, 'other')}
                                title="Enter manual split of pass & fail quantities"
                              >
                                Other
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px' }}>
                              <QcInfoTile label="Passed Qty" value={(job.qcPassedQty || 0) + ' ' + (job.unit || 'pcs')} color="#10b981" />
                              <QcInfoTile label="Failed Qty" value={(job.qcFailedQty || 0) + ' ' + (job.unit || 'pcs')} color="#ef4444" />
                              <QcInfoTile label="Checked At" value={qcFormatDateTime(job.qcCheckedAt)} mono />
                              <QcInfoTile label="Checked By" value={job.qcCheckedBy?.name || 'Quality Checker'} />
                            </div>

                            {job.qcRemarks && (
                              <div style={{
                                marginTop: '10px', fontSize: '0.82rem', color: 'var(--text-secondary)',
                                background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border)',
                                borderRadius: '8px', padding: '10px 12px'
                              }}>
                                <strong style={{ color: 'var(--text-dim)' }}>Remarks:</strong> {job.qcRemarks}
                              </div>
                            )}

                            <div style={{ marginTop: '10px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              <button
                                className="btn btn-sm btn-secondary"
                                style={{ fontWeight: 700, fontSize: '0.8rem' }}
                                onClick={() => handleOpenSupervisorQC(job, 'other')}
                                title="Record a fresh inspection for this build"
                              >
                                Re-check Quality
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <Pagination
                  currentPage={supervisorQcPage}
                  totalPages={Math.ceil(modelJobs.length / 5) || 1}
                  totalItems={modelJobs.length}
                  itemsPerPage={5}
                  onPageChange={setSupervisorQcPage}
                />
              </>
            );
          })()}
        </div>
      ) : null}

      {/* ASSIGN SUPERVISOR MODAL */}
      {showAssignSupervisorModal && (
        <ModalOverlay onClose={() => setShowAssignSupervisorModal(false)} title="Assign Inventory Shortage to Supervisor">
          <form onSubmit={handleAssignSupervisorSubmit}>
            <div className="form-group">
              <label className="form-label">Item / Product Name</label>
              <input type="text" className="form-input" readOnly value={selectedInventoryItem?.name || ''} style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--text-secondary)' }} />
            </div>
            <div className="form-group">
              <label className="form-label">Store Manager Remark</label>
              <div style={{ padding: '8px 12px', background: 'rgba(34, 197, 94, 0.08)', border: '1px solid rgba(34, 197, 94, 0.2)', borderRadius: '6px', color: '#22c55e', fontSize: '0.88rem' }}>
                ✅ {selectedInventoryItem?.storeRemarks || 'Urgent replenishment required'}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Assign Supervisor / Production Line *</label>
              <select className="form-input" required value={selectedSupervisor} onChange={e => setSelectedSupervisor(e.target.value)}>
                <option value="Supervisor Line 1">Supervisor Line 1 (Main Assembly)</option>
                <option value="Supervisor Line 2">Supervisor Line 2 (Shots Production)</option>
                <option value="Production Supervisor">Production Supervisor</option>
                <option value="Quality Supervisor">Quality Supervisor</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Production Manager Remarks (Urgent Basis) *</label>
              <textarea
                className="form-input"
                rows="3"
                required
                value={pmAssignRemarks}
                onChange={e => setPmAssignRemarks(e.target.value)}
                placeholder="e.g. Urgent basis priority production required for client order..."
              ></textarea>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button type="button" className="btn btn-cancel" onClick={() => setShowAssignSupervisorModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Assigning...' : 'Assign Supervisor'}</button>
            </div>
          </form>
        </ModalOverlay>
      )}

      {/* GATE VERIFY REMARK MODAL */}
      {showGateVerifyModal && (
        <ModalOverlay onClose={() => setShowGateVerifyModal(false)} title="Verify Gate shipment">
          <form onSubmit={handleApproveGate}>
            <div className="form-group">
              <label className="form-label">Verification remarks *</label>
              <textarea className="form-input" required rows="3" value={verificationRemarks} onChange={e => setVerificationRemarks(e.target.value)} placeholder="e.g. Nylon sacks quantity verified, bags checked, no damages found."></textarea>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyItems: 'flex-end', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button type="button" className="btn btn-cancel" onClick={() => setShowGateVerifyModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Verifying...' : 'Verify & Approve Shipment'}</button>
            </div>
          </form>
        </ModalOverlay>
      )}

      {/* MACHINE FORM MODAL */}
      {showMachineModal && (
        <ModalOverlay onClose={() => setShowMachineModal(false)} title={editingMachineId ? "Edit Belt Machine Status" : "Add Belt Production Machine"}>
          <form onSubmit={handleSaveMachine}>
            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: '16px', marginBottom: '16px' }}>

              {/* Left Column: Selector + Custom Input if active */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div className="form-group" style={{ position: 'relative', margin: 0 }} ref={dropdownRef}>
                  <label className="form-label">Machine Type *</label>

                  {/* Trigger Button */}
                  <div
                    onClick={() => setShowCustomDropdown(!showCustomDropdown)}
                    style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '10px 14px', background: 'var(--bg-input, rgba(255,255,255,0.02))',
                      border: '1px solid var(--border)', borderRadius: '8px', cursor: 'pointer',
                      height: '38px', fontSize: '0.9rem', color: 'var(--text-primary)'
                    }}
                  >
                    <span>{machineForm.name || '-- Select Machine --'}</span>
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>▼</span>
                  </div>

                  {/* Floating Category Menu */}
                  {showCustomDropdown && (
                    <div style={{
                      position: 'absolute', top: '70px', left: 0, width: '460px', height: '260px',
                      background: 'var(--bg-card, #1e293b)', border: '1px solid var(--border)',
                      borderRadius: '12px', boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
                      display: 'flex', zIndex: 1000, overflow: 'hidden'
                    }}>
                      {/* Left Panel: Categories */}
                      <div style={{
                        width: '200px', borderRight: '1px solid var(--border)',
                        padding: '8px 0', overflowY: 'auto', background: 'rgba(0,0,0,0.1)'
                      }}>
                        {Object.keys(MACHINE_CATEGORIES).map(cat => (
                          <div
                            key={cat}
                            onMouseEnter={() => {
                              setActiveHoverCategory(cat);
                            }}
                            style={{
                              padding: '10px 16px', fontSize: '0.85rem', cursor: 'pointer',
                              color: activeHoverCategory === cat ? 'var(--primary)' : 'var(--text-secondary)',
                              background: activeHoverCategory === cat ? 'rgba(249, 115, 22, 0.08)' : 'transparent',
                              fontWeight: activeHoverCategory === cat ? 600 : 500,
                              transition: 'all 0.15s ease'
                            }}
                          >
                            {cat}
                          </div>
                        ))}
                        <div
                          onMouseEnter={() => {
                            setActiveHoverCategory('custom');
                          }}
                          onClick={() => {
                            setIsCustomMachineName(true);
                            setMachineForm(prev => ({ ...prev, name: '' }));
                            setShowCustomDropdown(false);
                          }}
                          style={{
                            padding: '10px 16px', fontSize: '0.85rem', cursor: 'pointer',
                            color: activeHoverCategory === 'custom' ? 'var(--primary)' : 'var(--text-secondary)',
                            background: activeHoverCategory === 'custom' ? 'rgba(249, 115, 22, 0.08)' : 'transparent',
                            fontWeight: activeHoverCategory === 'custom' ? 600 : 500,
                            borderTop: '1px solid var(--border)'
                          }}
                        >
                          -- Custom Name --
                        </div>
                      </div>

                      {/* Right Panel: Sub-Machines */}
                      <div style={{ flex: 1, padding: '8px 0', overflowY: 'auto', background: 'transparent' }}>
                        {activeHoverCategory && activeHoverCategory !== 'custom' ? (
                          <>
                            <div style={{ padding: '8px 16px 4px', fontSize: '0.72rem', color: 'var(--text-dim)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              {activeHoverCategory}
                            </div>
                            {(MACHINE_CATEGORIES[activeHoverCategory] || []).map(sub => (
                              <div
                                key={sub}
                                onClick={() => {
                                  setMachineForm(prev => ({ ...prev, name: sub }));
                                  setIsCustomMachineName(false);
                                  setShowCustomDropdown(false);
                                }}
                                style={{
                                  padding: '8px 16px', fontSize: '0.85rem', cursor: 'pointer',
                                  color: machineForm.name === sub ? 'var(--primary)' : 'var(--text-primary)',
                                  background: machineForm.name === sub ? 'rgba(249, 115, 22, 0.04)' : 'transparent',
                                  border: machineForm.name === sub ? '1px solid var(--primary)' : '1px solid transparent',
                                  borderRadius: '6px',
                                  margin: '2px 8px',
                                  transition: 'all 0.15s ease'
                                }}
                                onMouseOver={e => {
                                  e.currentTarget.style.border = '1px solid var(--primary)';
                                  if (machineForm.name !== sub) {
                                    e.currentTarget.style.background = 'rgba(249, 115, 22, 0.04)';
                                  }
                                }}
                                onMouseOut={e => {
                                  e.currentTarget.style.border = machineForm.name === sub ? '1px solid var(--primary)' : '1px solid transparent';
                                  if (machineForm.name !== sub) {
                                    e.currentTarget.style.background = 'transparent';
                                  }
                                }}
                              >
                                {sub}
                              </div>
                            ))}
                          </>
                        ) : activeHoverCategory === 'custom' ? (
                          <div style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                            Click here to type a custom name.
                          </div>
                        ) : (
                          <div style={{ padding: '40px 16px', textAlign: 'center', color: 'var(--text-dim)', fontSize: '0.85rem' }}>
                            Hover over a category on the left to view machines
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {isCustomMachineName && (
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Custom Machine Name *</label>
                    <input
                      type="text"
                      className="form-input"
                      required
                      value={machineForm.name}
                      onChange={e => setMachineForm({ ...machineForm, name: e.target.value })}
                      placeholder="Enter custom machine name"
                    />
                  </div>
                )}
              </div>

              {/* Right Column: Status */}
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Operational Status *</label>
                <select className="form-input" value={machineForm.status} onChange={e => setMachineForm({ ...machineForm, status: e.target.value })}>
                  <option value="working">Working</option>
                  <option value="available">Available</option>
                  <option value="damaged">Damaged</option>
                  <option value="not found">Not Found</option>
                  <option value="idle">Idle</option>
                  <option value="destroyed">Destroyed</option>
                  <option value="running">In Progress (Running)</option>
                  <option value="broken">Broken (Damaged)</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label className="form-label">Production Capacity *</label>
                <input type="text" className="form-input" value={machineForm.capacity} onChange={e => setMachineForm({ ...machineForm, capacity: e.target.value })} placeholder="e.g. 200 meters/hour or 500 pcs/day" />
              </div>
              <div className="form-group">
                <label className="form-label">Production Line *</label>
                <select className="form-input" value={machineForm.productionLine} onChange={e => setMachineForm({ ...machineForm, productionLine: e.target.value })}>
                  <option value="Line 1">Production Line 1</option>
                  <option value="Line 2">Production Line 2</option>
                  <option value="Line 3">Production Line 3</option>
                  <option value="Line A">Line A (Molding)</option>
                  <option value="Line B">Line B (Extrusion)</option>
                  <option value="Line C">Line C (Assembly)</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label className="form-label">Model Number</label>
                <input type="text" className="form-input" value={machineForm.modelNumber} onChange={e => setMachineForm({ ...machineForm, modelNumber: e.target.value })} placeholder="e.g. BELT-VULC-300" />
              </div>
              <div className="form-group">
                <label className="form-label">Power Rating (e.g. 15 kW)</label>
                <input type="text" className="form-input" value={machineForm.powerRating} onChange={e => setMachineForm({ ...machineForm, powerRating: e.target.value })} placeholder="e.g. 15 kW" />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Last Service Date</label>
              <input type="date" className="form-input" value={machineForm.lastServiceDate} onChange={e => setMachineForm({ ...machineForm, lastServiceDate: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Maintenance Notes / Mold Config</label>
              <textarea className="form-input" value={machineForm.remarks} onChange={e => setMachineForm({ ...machineForm, remarks: e.target.value })} placeholder="Mold width configurations, tension calibration, or recent maintenance details..."></textarea>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button type="button" className="btn btn-cancel" onClick={() => setShowMachineModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Saving...' : 'Save Machine'}</button>
            </div>
          </form>
        </ModalOverlay>
      )}

      {/* START BUILD FORM MODAL */}
      {showBuildModal && (
        <ModalOverlay onClose={() => setShowBuildModal(false)} title={buildModalMode === 'create' ? "Configure Production Build Process" : "Edit Build Process Configuration"}>

          {buildModalMode === 'create' && (
            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label className="form-label">Select Pending Build Order *</label>
              <select
                className="form-input"
                required
                value={selectedJobId}
                onChange={e => setSelectedJobId(e.target.value)}
              >
                <option value="">-- Choose Pending Order --</option>
                {selectedJobId && selectedJobId.startsWith('shortage_') && selectedJob && (
                  <option value={selectedJobId}>
                    [SHORTAGE] {selectedJob.productName} (Qty: {selectedJob.orderQuantity} pcs)
                  </option>
                )}
                {buildJobs.filter(j => ['pending', 'shortage_reported'].includes(j.status) || j._id === selectedJobId).map(j => (
                  <option key={j._id} value={j._id}>
                    {j.productName} (Qty: {j.orderQuantity} pcs, Size: {j.productSize || 'Standard'}) - {j.status}
                  </option>
                ))}
              </select>
            </div>
          )}

          {buildModalMode === 'edit' && (
            <div style={{ background: 'var(--primary-glow)', padding: '14px', borderRadius: '8px', border: '1px solid var(--border-primary)', marginBottom: '20px' }}>
              <h4 style={{ color: 'var(--text-primary)', margin: '0 0 4px 0' }}>Editing Process: {selectedJob?.productName}</h4>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Target Qty: {selectedJob?.orderQuantity} pcs</div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginTop: '4px' }}>Original Status: <span style={{ textTransform: 'capitalize' }}>{selectedJob?.status?.replace('_', ' ')}</span></div>
            </div>
          )}

          {selectedJob ? (
            <form onSubmit={handleStartProcessBuild}>

              {/* Target Details for Create Mode */}
              {buildModalMode === 'create' && (
                <div style={{ background: 'var(--primary-glow)', padding: '14px', borderRadius: '8px', border: '1px solid var(--border-primary)', marginBottom: '20px' }}>
                  <h4 style={{ color: 'var(--text-primary)', margin: '0 0 4px 0' }}>Target Product: {selectedJob.productName}</h4>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Target Qty: {selectedJob.orderQuantity} pcs</div>
                </div>
              )}

              {/* Status Selection (Edit Mode only) */}
              {buildModalMode === 'edit' && (
                <div className="form-group" style={{ marginBottom: '20px' }}>
                  <label className="form-label">Supervise Status *</label>
                  <select
                    className="form-input"
                    required
                    value={buildStatus}
                    onChange={e => setBuildStatus(e.target.value)}
                  >
                    <option value="pending">Pending</option>
                    <option value="processing">Processing / In Production</option>
                    <option value="delayed">Delayed</option>
                    <option value="shortage_reported">Shortage Reported</option>
                    <option value="completed">Completed / Sent to Store</option>
                  </select>
                </div>
              )}

              {/* Machine Selection - batches/shots are no longer asked here.
                  They are captured at completion, right before Quality Check. */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px', marginBottom: '20px' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Assign Machine / Production Line *</label>
                  <select className="form-input" required value={selectedMachineId} onChange={e => setSelectedMachineId(e.target.value)}>
                    {(() => {
                      const availables = machines.filter(m => m.status === 'available');
                      if (availables.length === 0) {
                        return <option value="" disabled>-- No Machines Available (All Working/Busy) --</option>;
                      }
                      return (
                        <>
                          <option value="">-- Select Available Machine --</option>
                          {availables.map(m => (
                            <option key={m._id} value={m._id}>{m.name} (Cap: {m.capacity || 'N/A'})</option>
                          ))}
                        </>
                      );
                    })()}
                  </select>
                </div>
              </div>

              {/* Build Process Parameters */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Target Setup Time (sec)</label>
                  <input type="number" min="0" step="1" className="form-input" placeholder="e.g. 25" value={processSpecs.targetSetupTimeSec} onChange={e => setProcessSpecs(prev => ({ ...prev, targetSetupTimeSec: e.target.value }))} />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Resin Change (Plan/Mix)</label>
                  <select className="form-input" value={processSpecs.resinChange} onChange={e => setProcessSpecs(prev => ({ ...prev, resinChange: e.target.value }))}>
                    <option value="">-- Select --</option>
                    {RESIN_CHANGE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Die Shot Req</label>
                  <input type="number" min="0" step="1" className="form-input" placeholder="e.g. 500" value={processSpecs.dieShotReq} onChange={e => setProcessSpecs(prev => ({ ...prev, dieShotReq: e.target.value }))} />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Operator Name</label>
                  <input type="text" className="form-input" placeholder="Enter operator name" value={processSpecs.operatorName} onChange={e => setProcessSpecs(prev => ({ ...prev, operatorName: e.target.value }))} />
                </div>
              </div>

              {/* Standard Target Date & Time */}
              <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: '12px', marginBottom: '20px' }}>
                <div className="form-group">
                  <label className="form-label">Estimated Completion Date *</label>
                  <input type="date" className="form-input" required value={estimatedDate} onChange={e => setEstimatedDate(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Completion Time *</label>
                  <input type="time" className="form-input" required value={buildTime} onChange={e => setBuildTime(e.target.value)} />
                </div>
              </div>

              {/* Material Consumption Logs */}
              <div style={{ marginBottom: '20px' }}>
                <label className="form-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Material Inwards Log</span>
                  <button type="button" className="btn btn-secondary btn-sm" style={{ padding: '2px 8px', fontSize: '0.75rem' }} onClick={handleAddMaterial}>+ Add Material</button>
                </label>
                {materialsUsed.map((mat, idx) => {
                  const uniqueNames = Array.from(new Set(rawMaterials.map(m => m.name)));
                  const defaultOptions = ['Nylon', 'PVC Resin', 'Natural Rubber', 'Steel Wire', 'Polyester Fabric'];
                  defaultOptions.forEach(opt => {
                    if (!uniqueNames.includes(opt)) {
                      uniqueNames.push(opt);
                    }
                  });

                  // The model number / product name of the build being configured is a
                  // first-class option at the top of the list, so it shows by name
                  // instead of falling into the "-- Custom... --" bucket.
                  [selectedJob?.modelNumber, selectedJob?.productName].forEach(val => {
                    const clean = (val || '').trim();
                    if (clean && !uniqueNames.includes(clean)) {
                      uniqueNames.unshift(clean);
                    }
                  });

                  const isCustom = mat.isCustom || (mat.materialName !== '' && !uniqueNames.includes(mat.materialName) && mat.materialName !== 'Unspecified Raw Material');

                  return (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: isCustom ? '1fr 1fr 0.8fr 0.8fr auto' : '1.8fr 1fr 1fr auto', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                      <select className="form-input" style={{ padding: '8px 12px' }} value={isCustom ? 'Custom...' : (mat.materialName || '')} onChange={e => {
                        const updated = [...materialsUsed];
                        const val = e.target.value;
                        if (val === 'Custom...') {
                          updated[idx].materialName = '';
                          updated[idx].isCustom = true;
                        } else {
                          updated[idx].materialName = val;
                          updated[idx].isCustom = false;
                        }
                        setMaterialsUsed(updated);
                      }}>
                        <option value="">-- Choose Material --</option>
                        {uniqueNames.map(name => (
                          <option key={name} value={name}>{name}</option>
                        ))}
                        <option value="Custom...">-- Custom... --</option>
                      </select>

                      {isCustom && (
                        <input
                          type="text"
                          className="form-input"
                          style={{ padding: '8px 12px' }}
                          placeholder="Type material name..."
                          required
                          value={mat.materialName}
                          onChange={e => {
                            const updated = [...materialsUsed];
                            updated[idx].materialName = e.target.value;
                            updated[idx].isCustom = true;
                            setMaterialsUsed(updated);
                          }}
                        />
                      )}

                      <input type="number" className="form-input" style={{ padding: '8px 12px' }} placeholder="Qty" required value={mat.quantity} onChange={e => {
                        const updated = [...materialsUsed];
                        updated[idx].quantity = Number(e.target.value);
                        setMaterialsUsed(updated);
                      }} />
                      <select className="form-input" style={{ padding: '8px 12px' }} value={mat.unit} onChange={e => {
                        const updated = [...materialsUsed];
                        updated[idx].unit = e.target.value;
                        setMaterialsUsed(updated);
                      }}>
                        <option value="kg">kg</option>
                        <option value="pcs">pcs</option>
                        <option value="bags">bags</option>
                        <option value="liters">liters</option>
                      </select>
                      {materialsUsed.length > 1 && (
                        <button type="button" style={{ color: 'var(--danger)', background: 'none' }} onClick={() => handleRemoveMaterial(idx)}>
                          <HiOutlineTrash />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Flexible Custom Fields */}
              <div style={{ marginBottom: '20px' }}>
                <label className="form-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Custom Inputs / Build Specs</span>
                  <button type="button" className="btn btn-secondary btn-sm" style={{ padding: '2px 8px', fontSize: '0.75rem' }} onClick={handleAddCustomField}>+ Add Custom Field</button>
                </label>
                {customFields.map((field, idx) => (
                  <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.8fr auto', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                    <input type="text" className="form-input" style={{ padding: '8px 12px' }} placeholder="Field Label (e.g. Mold ID)" required value={field.key} onChange={e => {
                      const updated = [...customFields];
                      updated[idx].key = e.target.value;
                      setCustomFields(updated);
                    }} />
                    <input type="text" className="form-input" style={{ padding: '8px 12px' }} placeholder="Value (e.g. M-88)" required value={field.value} onChange={e => {
                      const updated = [...customFields];
                      updated[idx].value = e.target.value;
                      setCustomFields(updated);
                    }} />
                    <button type="button" style={{ color: 'var(--danger)', background: 'none' }} onClick={() => handleRemoveCustomField(idx)}>
                      <HiOutlineTrash />
                    </button>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                <button type="button" className="btn btn-cancel" onClick={() => setShowBuildModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : (buildModalMode === 'create' ? 'Load Production Process' : 'Save Process Updates')}
                </button>
              </div>
            </form>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-dim)', fontSize: '0.9rem' }}>
              Please select a pending build order above to configure details.
            </div>
          )}
        </ModalOverlay>
      )}

      {/* ===== COMPLETE BUILD MODAL - batches/shots asked before Quality Check ===== */}
      {showCompleteBuildModal && completingJob && (
        <ModalOverlay onClose={() => setShowCompleteBuildModal(false)} title="Complete Build - Send to Quality Check">
          <form onSubmit={handleSubmitCompleteBuild}>

            {/* What is being completed */}
            <div style={{
              background: 'rgba(249, 115, 22, 0.07)',
              border: '1px solid rgba(249, 115, 22, 0.25)',
              borderRadius: '10px',
              padding: '14px 16px',
              marginBottom: '20px'
            }}>
              <div style={{ fontWeight: 800, color: 'var(--text-primary)', fontSize: '1rem', marginBottom: '4px' }}>
                {completingJob.productName}
              </div>
              <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                <span><strong style={{ color: 'var(--primary)' }}>Model:</strong> {completingJob.modelNumber || 'Standard'}</span>
                <span><strong>Qty:</strong> {completingJob.orderQuantity} {completingJob.unit || 'pcs'}</span>
                <span><strong>Machine:</strong> {completingJob.machineName || completingJob.machineId?.name || 'Unassigned'}</span>
                <span><strong>Line:</strong> {completingJob.productionLine || 'Line 1'}</span>
                {getBuildProcessSpecs(completingJob).map(spec => (
                  <span key={spec.label}><strong>{spec.label}:</strong> {spec.value}</span>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                No. of Batches / Shots Completed <span style={{ color: 'var(--danger)' }}>*</span>
              </label>
              <input
                type="number"
                className="form-input"
                min="1"
                step="1"
                required
                autoFocus
                placeholder="e.g. 12"
                value={completeForm.completedShots}
                onChange={e => setCompleteForm(prev => ({ ...prev, completedShots: e.target.value }))}
              />
              <div style={{ fontSize: '0.76rem', color: 'var(--text-dim)', marginTop: '6px' }}>
                How many batches / shot runs did this build actually take? This is recorded against the
                build and shown to the Quality Checker.
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Completion Note (optional)</label>
              <textarea
                className="form-input"
                rows={3}
                placeholder="Anything the Quality Checker should know about this run..."
                value={completeForm.completionRemarks}
                onChange={e => setCompleteForm(prev => ({ ...prev, completionRemarks: e.target.value }))}
              />
            </div>

            <div style={{
              background: 'rgba(59, 130, 246, 0.08)',
              border: '1px solid rgba(59, 130, 246, 0.25)',
              borderRadius: '8px',
              padding: '10px 12px',
              fontSize: '0.8rem',
              color: 'var(--text-secondary)',
              marginBottom: '8px'
            }}>
              On save this build is marked <strong>Completed</strong> and moves straight into
              <strong> Quality Check</strong>.
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button type="button" className="btn btn-cancel" onClick={() => setShowCompleteBuildModal(false)} disabled={submitting}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={submitting} style={{ fontWeight: 800 }}>
                {submitting ? 'Saving...' : 'Complete & Send to QC'}
              </button>
            </div>
          </form>
        </ModalOverlay>
      )}

      {/* ===== ADD ITEMS & VERIFY MODAL (SUPERVISOR) ===== */}
      {showAddItemsModal && selectedEntryForItems && (
        <ModalOverlay onClose={() => setShowAddItemsModal(false)} title="Add Items & Verify Gate Entry">
          <div style={{
            background: 'var(--primary-glow)', border: '1px solid var(--border-primary)',
            borderRadius: '12px', padding: '16px', marginBottom: '20px'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '0.85rem' }}>
              <div><span style={{ color: 'var(--text-dim)' }}>Bill No:</span> <span style={{ color: 'var(--primary)', fontWeight: 600 }}>{selectedEntryForItems.billNumber}</span></div>
              <div><span style={{ color: 'var(--text-dim)' }}>Vendor:</span> <span style={{ color: 'var(--text-primary)' }}>{selectedEntryForItems.vendorName}</span></div>
              <div><span style={{ color: 'var(--text-dim)' }}>Original Material:</span> <span style={{ color: 'var(--accent)', textTransform: 'capitalize' }}>{selectedEntryForItems.materialType}</span></div>
              <div><span style={{ color: 'var(--text-dim)' }}>Original Qty:</span> <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{selectedEntryForItems.quantity} {selectedEntryForItems.unit}</span></div>
            </div>
          </div>

          <form onSubmit={handleSaveAndVerify}>
            <div className="form-group">
              <label className="form-label">Item / Material Name *</label>
              <input className="form-input" required value={itemsForm.name}
                onChange={(e) => setItemsForm({ ...itemsForm, name: e.target.value })} placeholder="e.g., Nylon Drag Chain" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div className="form-group">
                <label className="form-label">Item Type *</label>
                <select className="form-input" value={itemsForm.type}
                  onChange={(e) => {
                    const typeVal = e.target.value;
                    const defaultUnit = typeVal === 'raw_material' ? 'kg' : 'pcs';
                    setItemsForm(prev => ({ ...prev, type: typeVal, unit: defaultUnit }));
                  }}
                  style={{ cursor: 'pointer' }}>
                  <option value="raw_material">Raw Material</option>
                  <option value="finished_good">Finished Good</option>
                  <option value="hardware">Hardware / Tools</option>
                  <option value="custom">Custom Tooling</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Stock Unit *</label>
                <select className="form-input" required value={itemsForm.unit}
                  onChange={(e) => setItemsForm({ ...itemsForm, unit: e.target.value })} style={{ cursor: 'pointer' }}>
                  <option value="kg">kg (Kilograms)</option>
                  <option value="pcs">pcs (Pieces)</option>
                  <option value="meters">meters (Meters)</option>
                  <option value="boxes">boxes (Boxes)</option>
                  <option value="pkt">pkt (Packets)</option>
                  <option value="liters">liters (Liters)</option>
                  <option value="bags">bags (Bags)</option>
                  <option value="rolls">rolls (Rolls)</option>
                  <option value="sets">sets (Sets)</option>
                  <option value="tons">tons (Tons)</option>
                </select>
              </div>
            </div>


            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div className="form-group">
                <label className="form-label">Stock Quantity *</label>
                <input className="form-input" type="number" step="0.01" min="0.01" required value={itemsForm.quantity}
                  onChange={(e) => setItemsForm({ ...itemsForm, quantity: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Warehouse Location</label>
                <input className="form-input" value={itemsForm.location}
                  onChange={(e) => setItemsForm({ ...itemsForm, location: e.target.value })} placeholder="e.g., Row A - Rack 2" />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Size / Gauge Specifications</label>
              <input className="form-input" value={itemsForm.size}
                onChange={(e) => setItemsForm({ ...itemsForm, size: e.target.value })} placeholder="e.g., 35mm x 100mm" />
            </div>

            <div className="form-group">
              <label className="form-label">Item Specs / Description</label>
              <textarea className="form-input" rows="2" value={itemsForm.description}
                onChange={(e) => setItemsForm({ ...itemsForm, description: e.target.value })} placeholder="Specifications..."></textarea>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-cancel" onClick={() => setShowAddItemsModal(false)}>Cancel</button>
              <button type="submit" className="btn" style={{ background: 'linear-gradient(135deg, #64748b, #475569)', color: '#ffffff', border: 'none' }} disabled={submitting}>
                {submitting ? 'Saving & Verifying...' : 'Save & Verify'}
              </button>
            </div>
          </form>
        </ModalOverlay>
      )}
      {/* ADD/EDIT MATERIAL MODAL */}
      {showMaterialModal && (
        <ModalOverlay onClose={() => setShowMaterialModal(false)} title={editingMaterialId ? "Edit Material Record" : "Add New Material to Inventory"}>
          <form onSubmit={handleSaveMaterial}>
            <div className="form-group">
              <label className="form-label">Material Name *</label>
              <input
                type="text"
                className="form-input"
                required
                value={materialForm.name}
                onChange={e => setMaterialForm({ ...materialForm, name: e.target.value })}
                placeholder="e.g. Nylon Sacks, M10 Screws"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label className="form-label">Quantity *</label>
                <input
                  type="number"
                  className="form-input"
                  required
                  min="0"
                  value={materialForm.quantity}
                  onChange={e => setMaterialForm({ ...materialForm, quantity: Number(e.target.value) })}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Unit of Measurement *</label>
                <select
                  className="form-input"
                  required
                  value={materialForm.unit}
                  onChange={e => setMaterialForm({ ...materialForm, unit: e.target.value })}
                >
                  <option value="kg">kg (Kilogram)</option>
                  <option value="pcs">pcs (Pieces)</option>
                  <option value="pkt">pkt (Packets)</option>
                  <option value="meters">meters (Meters)</option>
                  <option value="liters">liters (Liters)</option>
                  <option value="bags">bags (Bags)</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label className="form-label">Size Specification</label>
                <input
                  type="text"
                  className="form-input"
                  value={materialForm.size}
                  onChange={e => setMaterialForm({ ...materialForm, size: e.target.value })}
                  placeholder="e.g. 10mm, 2m length"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Location / Rack</label>
                <input
                  type="text"
                  className="form-input"
                  value={materialForm.location}
                  onChange={e => setMaterialForm({ ...materialForm, location: e.target.value })}
                  placeholder="e.g. Rack A - Bin 3"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Description / Remarks</label>
              <textarea
                className="form-input"
                rows="3"
                value={materialForm.description}
                onChange={e => setMaterialForm({ ...materialForm, description: e.target.value })}
                placeholder="Details about quality, batch number, or storage details..."
              ></textarea>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-cancel" onClick={() => setShowMaterialModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? 'Saving...' : 'Save Material'}
              </button>
            </div>
          </form>
        </ModalOverlay>
      )}

      {/* VIEW PRODUCT SPECS MODAL */}
      {showViewSpecsModal && viewSpecsItem && (
        <ModalOverlay onClose={() => setShowViewSpecsModal(false)} title="Product Specification Details">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={{ background: 'rgba(255,255,255,0.03)', padding: '12px 16px', borderRadius: '8px', border: '1px solid var(--border-light)' }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)', textTransform: 'uppercase' }}>Product Name</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary)', marginTop: '2px' }}>{viewSpecsItem.name}</div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={{ background: 'rgba(255,255,255,0.02)', padding: '10px 14px', borderRadius: '6px', border: '1px solid var(--border-light)' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Model Number</span>
                <div style={{ fontWeight: 600, color: 'var(--primary)', marginTop: '2px' }}>{viewSpecsItem.modelNumber || 'N/A'}</div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.02)', padding: '10px 14px', borderRadius: '6px', border: '1px solid var(--border-light)' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Width (Inner)</span>
                <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginTop: '2px' }}>{viewSpecsItem.width || viewSpecsItem.size || 'N/A'}</div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.02)', padding: '10px 14px', borderRadius: '6px', border: '1px solid var(--border-light)' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Bending Radius (R)</span>
                <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginTop: '2px' }}>{viewSpecsItem.radius || 'N/A'}</div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.02)', padding: '10px 14px', borderRadius: '6px', border: '1px solid var(--border-light)' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Type (Open/Closed)</span>
                <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginTop: '2px' }}>{viewSpecsItem.chainType || viewSpecsItem.designType || 'OP'}</div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.02)', padding: '10px 14px', borderRadius: '6px', border: '1px solid var(--border-light)' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>No. of Links (per m)</span>
                <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginTop: '2px' }}>{viewSpecsItem.numberOfLinks || 'N/A'}</div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.02)', padding: '10px 14px', borderRadius: '6px', border: '1px solid var(--border-light)' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Length (mm / m)</span>
                <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginTop: '2px' }}>{viewSpecsItem.length || 'N/A'}</div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.02)', padding: '10px 14px', borderRadius: '6px', border: '1px solid var(--border-light)' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Mounting Bracket</span>
                <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginTop: '2px' }}>{viewSpecsItem.mountingBracketType || 'N/A'}</div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.02)', padding: '10px 14px', borderRadius: '6px', border: '1px solid var(--border-light)' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Accessories (Separator)</span>
                <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginTop: '2px' }}>{viewSpecsItem.accessories || viewSpecsItem.vs || viewSpecsItem.hs || 'None'}</div>
              </div>
            </div>

            {viewSpecsItem.storeRemarks && (
              <div style={{ padding: '10px 14px', background: 'rgba(34, 197, 94, 0.08)', border: '1px solid rgba(34, 197, 94, 0.2)', borderRadius: '6px', color: '#22c55e', fontSize: '0.88rem' }}>
                ✅ <strong>Store Remark:</strong> {viewSpecsItem.storeRemarks}
              </div>
            )}
            {viewSpecsItem.pmRemarks && (
              <div style={{ padding: '10px 14px', background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.2)', borderRadius: '6px', color: '#f59e0b', fontSize: '0.88rem' }}>
                🚨 <strong>PM Remark:</strong> {viewSpecsItem.pmRemarks}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setShowViewSpecsModal(false)}>Close</button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* PLAN PRODUCTION MODAL */}
      {showPlanModal && selectedShortage && (
        <ModalOverlay onClose={() => setShowPlanModal(false)} title={`Plan Production: ${selectedShortage.modelNumber}`}>
          <form onSubmit={handlePlanShortage}>
            <div style={{ background: 'var(--bg-input)', padding: '16px', borderRadius: '8px', marginBottom: '20px', border: '1px solid var(--border)' }}>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Shortage Quantity:</div>
              <div style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--danger)' }}>{selectedShortage.shortageMeters} meters</div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)', marginTop: '4px' }}>Ref Order: {selectedShortage.orderId?.orderNumber}</div>
            </div>

            <div className="form-group">
              <label className="form-label" style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Planned Shot Count *</span>
                <button type="button" style={{ color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }} onClick={handleAskAI} disabled={aiLoading}>
                  {aiLoading ? '🤖 Calculating...' : '🤖 Predict shots with AI'}
                </button>
              </label>
              <input type="number" className="form-input" required placeholder="Number of injection shots needed" value={planForm.plannedShots} onChange={e => setPlanForm({ ...planForm, plannedShots: e.target.value })} />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyItems: 'flex-end', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button type="button" className="btn btn-cancel" onClick={() => setShowPlanModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Approve & Release Plan'}
              </button>
            </div>
          </form>
        </ModalOverlay>
      )}

      {/* ===== VIEW PRODUCT SPECS MODAL (NON-EDITABLE) ===== */}
      {showViewSpecsModal && viewSpecsItem && (
        <ModalOverlay onClose={() => setShowViewSpecsModal(false)} title={`Product Specifications: ${viewSpecsItem.productName || viewSpecsItem.itemName || viewSpecsItem.name || 'Drag Chain Item'}`}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            
            {/* Header Badge Card */}
            <div style={{ background: 'rgba(59, 130, 246, 0.08)', border: '1px solid rgba(59, 130, 246, 0.2)', padding: '16px', borderRadius: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>Product Name</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--text-primary)' }}>{viewSpecsItem.productName || viewSpecsItem.itemName || viewSpecsItem.name || 'Drag Chain'}</div>
              </div>
              <span className="azure-badge success" style={{ textTransform: 'uppercase', fontSize: '0.85rem' }}>
                {viewSpecsItem.status || viewSpecsItem.productionStatus || 'Pre-Selected Specification'}
              </span>
            </div>

            {/* Grid Specs */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              
              <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-light)', padding: '12px', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Model Number</div>
                <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#3b82f6' }}>{viewSpecsItem.modelNumber || viewSpecsItem.planId?.modelNumber || 'N/A'}</div>
              </div>

              <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-light)', padding: '12px', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Item Type / Category</div>
                <div style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)', textTransform: 'uppercase' }}>{(viewSpecsItem.type || viewSpecsItem.itemType || 'finished_good').replace('_', ' ')}</div>
              </div>

              <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-light)', padding: '12px', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Inner Size</div>
                <div style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)' }}>{viewSpecsItem.size || viewSpecsItem.productSize || 'N/A'}</div>
              </div>

              <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-light)', padding: '12px', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Outer Size (Auto-Filled)</div>
                <div style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-secondary)' }}>{viewSpecsItem.description?.replace('Outer: ', '') || 'N/A'}</div>
              </div>

              <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-light)', padding: '12px', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Bending Radius</div>
                <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#f97316' }}>{viewSpecsItem.radius || 'N/A'}</div>
              </div>

              <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-light)', padding: '12px', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Vertical Separator (VS)</div>
                <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)' }}>{viewSpecsItem.vs || 'N/A'}</div>
              </div>

              <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-light)', padding: '12px', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Horizontal Separator (HS)</div>
                <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)' }}>{viewSpecsItem.hs || 'N/A'}</div>
              </div>

              <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-light)', padding: '12px', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Strain Reliever (SR)</div>
                <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)' }}>{viewSpecsItem.sr || 'N/A'}</div>
              </div>

              <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-light)', padding: '12px', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Required / Order Quantity</div>
                <div style={{ fontSize: '0.95rem', fontWeight: 800, color: '#ef4444' }}>{viewSpecsItem.quantity || viewSpecsItem.orderQuantity || getRequiredQty(viewSpecsItem)} {viewSpecsItem.unit || 'pcs'}</div>
              </div>

              <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-light)', padding: '12px', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Almirah Location</div>
                <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)' }}>{viewSpecsItem.almirah || 'N/A'}</div>
              </div>

              <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-light)', padding: '12px', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Rack / Shelf Location</div>
                <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)' }}>{viewSpecsItem.rack || 'N/A'}</div>
              </div>

            </div>

            {/* Remarks / Notes */}
            {(viewSpecsItem.remarks || viewSpecsItem.storeRemarks || viewSpecsItem.pmRemarks) && (
              <div style={{ background: 'rgba(249, 115, 22, 0.05)', border: '1px solid rgba(249, 115, 22, 0.15)', padding: '12px 16px', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.78rem', color: 'var(--primary)', fontWeight: 600, marginBottom: '4px' }}>📝 Notes & Specific Remarks</div>
                <div style={{ fontSize: '0.88rem', color: 'var(--text-primary)' }}>
                  {viewSpecsItem.remarks || viewSpecsItem.storeRemarks || viewSpecsItem.pmRemarks}
                </div>
              </div>
            )}

            {/* Footer Close Button */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button type="button" className="btn btn-cancel" onClick={() => setShowViewSpecsModal(false)}>Close View</button>
            </div>

          </div>
        </ModalOverlay>
      )}

      {/* ===== ADMIN EDIT SHORTAGE LOG MODAL ===== */}
      {showShortageEditModal && (
        <ModalOverlay onClose={() => setShowShortageEditModal(false)} title="Edit Shortage Task Entry (Admin Only)">
          <form onSubmit={handleSaveEditShortageLog}>
            <div className="form-group">
              <label className="form-label">Item / Product Name *</label>
              <input type="text" className="form-input" required value={shortageEditForm.itemName} onChange={e => setShortageEditForm({ ...shortageEditForm, itemName: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Shortage Quantity ({shortageEditForm.unit || 'pcs'}) *</label>
              <input type="number" className="form-input" required min={1} value={shortageEditForm.quantity} onChange={e => setShortageEditForm({ ...shortageEditForm, quantity: Number(e.target.value) })} />
            </div>
            <div className="form-group">
              <label className="form-label">Assigned To *</label>
              <select className="form-input" value={shortageEditForm.assignedTo} onChange={e => setShortageEditForm({ ...shortageEditForm, assignedTo: e.target.value })}>
                <option value="supervisor">Supervisor (Production Line)</option>
                <option value="sales">Sales Officer (Purchase Sourcing)</option>
                <option value="store_manager">Store Manager</option>
                <option value="unassigned">Unassigned</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Status *</label>
              <select className="form-input" value={shortageEditForm.status} onChange={e => setShortageEditForm({ ...shortageEditForm, status: e.target.value })}>
                <option value="pending">Pending</option>
                <option value="assigned">Assigned</option>
                <option value="ordered">Ordered</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Remarks</label>
              <textarea className="form-input" rows="2" value={shortageEditForm.remarks} onChange={e => setShortageEditForm({ ...shortageEditForm, remarks: e.target.value })} placeholder="Notes..."></textarea>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button type="button" className="btn btn-cancel" onClick={() => setShowShortageEditModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Saving...' : 'Save Changes'}</button>
            </div>
          </form>
        </ModalOverlay>
      )}

      {/* SUPERVISOR QUALITY CHECK MODAL */}
      {showSupervisorQcModal && selectedSupervisorQcJob && (
        <ModalOverlay onClose={() => setShowSupervisorQcModal(false)} title={`Quality Check - ${selectedSupervisorQcJob.productName}`}>
          <form onSubmit={handleSubmitSupervisorQC}>
            <div style={{ marginBottom: '16px', background: 'rgba(255, 255, 255, 0.03)', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>Product Name:</span>
                <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{selectedSupervisorQcJob.productName}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>Model Number:</span>
                <span className="azure-badge primary" style={{ fontWeight: 700 }}>
                  {selectedSupervisorQcJob.modelNumber || selectedSupervisorQcJob.productModel || 'Standard'}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>Total Produced Quantity:</span>
                <span style={{ fontWeight: 700, color: '#22c55e', fontSize: '0.95rem' }}>
                  {selectedSupervisorQcJob.orderQuantity || 0} units
                </span>
              </div>
              {getBuildProcessSpecs(selectedSupervisorQcJob).map(spec => (
                <div key={spec.label} style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
                  <span style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>{spec.label}:</span>
                  <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{spec.value}</span>
                </div>
              ))}
            </div>

            {/* ACTION SELECTOR PILLS */}
            <div style={{ marginBottom: '16px' }}>
              <label className="form-label" style={{ marginBottom: '8px', display: 'block' }}>QC Decision Outcome *</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => handleSwitchSupervisorQcAction('pass')}
                  style={{
                    padding: '10px',
                    borderRadius: '8px',
                    border: supervisorQcAction === 'pass' ? '2px solid #10b981' : '1px solid var(--border)',
                    background: supervisorQcAction === 'pass' ? 'rgba(16, 185, 129, 0.15)' : 'transparent',
                    color: supervisorQcAction === 'pass' ? '#10b981' : 'var(--text-dim)',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <HiOutlineCheck style={{ fontSize: '1.2rem' }} />
                  <span>Pass (100%)</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleSwitchSupervisorQcAction('fail')}
                  style={{
                    padding: '10px',
                    borderRadius: '8px',
                    border: supervisorQcAction === 'fail' ? '2px solid #ef4444' : '1px solid var(--border)',
                    background: supervisorQcAction === 'fail' ? 'rgba(239, 68, 68, 0.15)' : 'transparent',
                    color: supervisorQcAction === 'fail' ? '#ef4444' : 'var(--text-dim)',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <HiOutlineX style={{ fontSize: '1.2rem' }} />
                  <span>Fail (100%)</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleSwitchSupervisorQcAction('other')}
                  style={{
                    padding: '10px',
                    borderRadius: '8px',
                    border: supervisorQcAction === 'other' ? '2px solid #a855f7' : '1px solid var(--border)',
                    background: supervisorQcAction === 'other' ? 'rgba(168, 85, 247, 0.15)' : 'transparent',
                    color: supervisorQcAction === 'other' ? '#a855f7' : 'var(--text-dim)',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <HiOutlineAdjustments style={{ fontSize: '1.2rem' }} />
                  <span>Other (Split)</span>
                </button>
              </div>
            </div>

            {/* QUANTITIES */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label" style={{ color: '#10b981', fontWeight: 600 }}>
                  Passed Quantity (Units) *
                </label>
                <input
                  type="number"
                  min="0"
                  max={selectedSupervisorQcJob.orderQuantity || undefined}
                  className="form-input"
                  required
                  value={supervisorQcForm.passedQuantity}
                  onChange={e => setSupervisorQcForm({ ...supervisorQcForm, passedQuantity: e.target.value })}
                  readOnly={supervisorQcAction === 'pass'}
                  style={{
                    borderColor: '#10b981',
                    background: supervisorQcAction === 'pass' ? 'rgba(16, 185, 129, 0.08)' : undefined,
                    fontWeight: 700
                  }}
                />
                <span style={{ fontSize: '0.72rem', color: 'var(--text-dim)', marginTop: '2px', display: 'block' }}>
                  Will be credited into Store Unified Inventory
                </span>
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label" style={{ color: '#ef4444', fontWeight: 600 }}>
                  Failed Quantity (Units) *
                </label>
                <input
                  type="number"
                  min="0"
                  max={selectedSupervisorQcJob.orderQuantity || undefined}
                  className="form-input"
                  required
                  value={supervisorQcForm.failedQuantity}
                  onChange={e => setSupervisorQcForm({ ...supervisorQcForm, failedQuantity: e.target.value })}
                  readOnly={supervisorQcAction === 'fail'}
                  style={{
                    borderColor: '#ef4444',
                    background: supervisorQcAction === 'fail' ? 'rgba(239, 68, 68, 0.08)' : undefined,
                    fontWeight: 700
                  }}
                />
                <span style={{ fontSize: '0.72rem', color: 'var(--text-dim)', marginTop: '2px', display: 'block' }}>
                  Will auto-create rebuild assigned to Supervisor
                </span>
              </div>
            </div>

            {/* MANDATORY REMARKS */}
            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label className="form-label" style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
                Quality Check Remarks * (Mandatory for All Decisions)
              </label>
              <textarea
                className="form-input"
                rows="3"
                required
                placeholder="Enter mandatory inspection remarks, test observations, reason for pass/fail/other..."
                value={supervisorQcForm.remarks}
                onChange={e => setSupervisorQcForm({ ...supervisorQcForm, remarks: e.target.value })}
                style={{
                  border: !supervisorQcForm.remarks.trim() ? '1px solid #ef4444' : '1px solid var(--border)',
                  background: 'rgba(255,255,255,0.03)'
                }}
              />
              {!supervisorQcForm.remarks.trim() && (
                <span style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '4px', display: 'block' }}>
                  ⚠️ Remark is strictly required to proceed.
                </span>
              )}
            </div>

            {/* SUBMIT BUTTON - MUST BE EXACTLY "Send to Store" AS REQUESTED */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                type="button"
                className="btn btn-cancel"
                onClick={() => setShowSupervisorQcModal(false)}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting || !supervisorQcForm.remarks.trim()}
                style={{
                  background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                  borderColor: '#22c55e',
                  fontWeight: 700,
                  padding: '8px 20px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                {submitting ? 'Processing...' : 'Send to Store'}
              </button>
            </div>
          </form>
        </ModalOverlay>
      )}
    </DashboardLayout>
  );
};


const ModalOverlay = ({ children, onClose, title }) => (
  <div onClick={(e) => e.target === e.currentTarget && onClose()} style={{
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px'
  }}>
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '16px',
      width: '100%', maxWidth: '520px', maxHeight: '90vh', display: 'flex', flexDirection: 'column',
      overflow: 'hidden', boxShadow: '0 25px 60px rgba(0,0,0,0.5)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        <h3 style={{ fontSize: '1.2rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>{title}</h3>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-dim)', fontSize: '1.4rem', cursor: 'pointer' }}><HiOutlineX /></button>
      </div>
      <div style={{ padding: '24px', overflowY: 'auto', overflowX: 'hidden', flex: 1 }}>
        {children}
      </div>
    </div>
  </div>
);

export default SupervisorDashboard;
